/* eslint-disable no-undef */
import { useWorkflowCredits } from 'hooks/useWorkflowCredits';
import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import { iFollowupItem, iFollowupOption } from 'interfaces/iFollowupItem';
import CreditsAPI from 'api/CreditsAPI';
import { clearWorkflowRunId, getWorkflowRunId } from 'utils/workflowRunId';
import { useSnackbar } from 'notistack';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { GlobalContext } from 'providers/GlobalProvider';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  Prompt,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import format from 'date-fns/format';
import { useWebSocketFetcher } from 'hooks/useWebSocketFetcher';
import { iStreamedResponse } from 'interfaces/iStreamedResponse';

import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import CasesAPI from 'api/CasesAPI';
import { loadDraft, registerRecoverable } from 'services/sessionRecovery';
import {
  clearExportClassifyHandoff,
  extractTenDigitCode,
  saveExportClassifyHandoff,
  stripCommodityCodes,
  takeExportClassifyHandoff,
} from 'utils/exportClassifyHandoff';
import {
  EXPORT_CHAT_TRANSCRIPT_KEY,
  EXPORT_INITIAL_REQUEST_KEY,
  isExportCommandLabel,
  savedExportChatTranscript,
  savedExportOpeningMessage,
  serializeExportChatTranscript,
} from 'utils/exportChatTranscript';
import { ExportHubAPI, ExportDatabase } from 'api/ExportHubAPI';
import { Dialog, DialogContent, DialogTitle, Modal } from '@mui/material';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import ExportResultsComp from './ExportResultsComp';
import {
  isPhaseOrPartNotAssessed,
  isPhaseNotRequested,
  phaseNotPerformedReason,
} from 'utils/exportAssessment';
import {
  ExportScopeChoice,
  ExportScopePhase,
  EXPORT_SCOPE_CHIP_LABEL,
  scopeAutoIncluded,
  scopeControlFields,
} from 'utils/exportScope';
import { scheduleBTimelineDetail } from 'utils/scheduleBCodeResult';
import ExportLanding from './ExportLanding';
import FollowupQuestionCard, { isCardFollowup } from './FollowupQuestionCard';
import CountryScreeningResults from './CountryScreeningResults';
import CountryControlListResults from './CountryControlListResults';
import ProductProfileResults from './ProductProfileResults';
import ScheduleBResults from './ScheduleBResults';
import NRCResults from './NRCResults';
import AssessmentStatusBadge from './AssessmentStatusBadge';
import ITARResults from './ITARResults';
import CCLResults from './CCLResults';
import { Tooltip } from '@mui/material';
import RequiredLicense from 'pages/assign/RequiredLicense';
import TrialRunUsedPanel from 'components/Workflows/TrialRunUsedPanel';
import { LicenseType } from 'pages/assign/LicenseBadge';
import {
  WorkflowAssistantHeader,
  WorkflowComposer,
  WorkflowNotice,
  WorkflowCreditWarning,
  WorkflowPageHeader,
  WorkflowProgressHeader,
  WorkflowProgressStep,
  WorkflowProgressTimeline,
  WorkflowStreamingResponse,
  WorkflowStepStatus,
  useWorkflowChatScroll,
  useScrollWorkflowIntoView,
} from 'components/Workflows/StandardWorkflowLayout';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

// A single line in the "Thinking" progress panel, tagged with its level in the
// phase → group → step hierarchy. `phase` = the per-phase overview (the overall
// goal, e.g. "Starting the ITAR / USML review…"); `group` = a sub-stage heading
// (the first line of each distinct backend current_step, e.g. "Checking ITAR/USML
// controls…"); `step` = the fine-grained lines beneath a group.
// Named `EarThoughtStep` to avoid shadowing the unrelated `ThoughtStep` exported
// by the Classify HTS chat. `phaseKey` is only set on `phase`-level entries and
// identifies which phase the heading belongs to, for the chat-side heading and
// the right-rail-click-to-scroll behavior.
type EarThoughtStep = {
  // `finding` = a DETERMINATION (the RESULT of a step, e.g. the ITAR §126.1 verdict),
  // rendered as a distinct, prominent line so users see what the agent found — not just
  // what it's doing. Emitted by the backend as a `step_finding` event.
  text: string;
  level: 'phase' | 'group' | 'step' | 'finding';
  phaseKey?: ExportPhaseKey;
};

interface IMessage {
  id: number;
  sender: 'user' | 'bot';
  text: string | JSX.Element;
  type?: string;
  stage?: iStreamedResponse['stage'];
  event?: iStreamedResponse['event'];
  payload?: any;
  options?: string[];
  thoughtSteps?: EarThoughtStep[];
  thoughtSeconds?: number;
}

type EarStage = Exclude<iStreamedResponse['stage'], 'final' | undefined>;
const EAR_STAGE_ORDER = [
  'profile',
  'schedule_b',
  'country_screening',
  'export_screening',
  'nrc',
  'itar',
  'ccl',
] as const;
type WorkflowCommandAction = 'next' | 'run_all' | 'run_step' | 'goto';
// "country" is the Country and Control List Check (UX redesign 2026-10-03): the former
// "Country Screening" and "Sensitive Tech Jurisdictions" (export_screening) phases merged into one.
// Its UI data still spans data.country_screening + data.export_screening, and old saved runs /
// frames can still name "export_screening" — toExportPhaseKey maps those onto "country".
type ExportPhaseKey = 'profile' | 'schedule_b' | 'country' | 'nrc' | 'itar' | 'ccl';
type ExportPhaseStatus = Record<ExportPhaseKey, string>;

const LEGACY_EXPORT_PHASE_ALIASES: Record<string, ExportPhaseKey> = {
  export_screening: 'country',
  country_screening: 'country',
};

const EXPORT_CHAT_RECOVERY_KEY = 'workflow-export-chat';
const EXPORT_DATA_BOUNDARY_NOTICE =
  'Use high-level transaction details only. Do not paste patient records or PHI, raw research datasets, detailed laboratory procedures, or controlled technical files.';

// Deliberately excludes billing/credit-charge state (chargedStages,
// workflowChargeKey, insufficientCreditsOpen, and the various
// *StagesRef guard Sets in resetEarSession) -- restoring stale charge
// tracking from a client-side snapshot could desync it from what the server
// actually billed. Those stay derived fresh on every mount, same as today.
// lastSavedWorkflowSignature is also deliberately left out: the whole point
// of a recovery draft is unsaved work, so it should read as unsaved after
// restore, same as it genuinely is.
interface ExportChatRecoverableState {
  messages: IMessage[];
  input: string;
  currentStage: number;
  workflowState: any;
  workflowData: any;
  userProductDesc: string;
  ticketType: string;
  followupItems: iFollowupItem[];
  clfId: string | null;
  cmdtDesc: string | null;
  HTSCode: string | null;
  originCntry: string;
  explanatoryNotes: string | null;
  sectionNotes: string | null;
  crossLinks: string[];
  ticketId: string;
  newTicketName: string;
  scheduleBStatus: string;
  countryScreeningStatus: string;
  profileStatus: string;
  // Pre-merge drafts only (the control-list part now shares the country status).
  exportScreeningStatus?: string;
  nrcStatus: string;
  itarStatus: string;
  earStatus: string;
  uiActivePhase: ExportPhaseKey | '';
  // The verbatim opening message (Bug 9) — kept so a remount (Classify round trip) saves it
  // even before the next server echo refills the ref. Optional: older drafts lack it.
  initialRequest?: string;
}

// A message's `text` can in principle be a JSX.Element, which can't survive a
// JSON round-trip (and wouldn't mean anything after a fresh page load even if
// it did) -- drop any that aren't plain strings rather than let JSON.stringify
// throw or silently corrupt the draft.
// Slice B: the answer id the server reads as "an HTS code from the Classify tool" (a fresh
// attempt, never re-asked for its type) — mirrors CLASSIFY_RETURN_OPTION_ID in export_workflow.py.
const CLASSIFY_RETURN_OPTION_ID = 'classify_return';

// The paste card shown after the Classify tool (screen 4). It answers the question that was
// pending when the user left; "Open the Classify tool again" restarts the round trip and "I have a
// Schedule B code instead" moves to the composer (a typed code goes through the normal check).
const buildClassifyReturnCardItem = (question: string): iFollowupItem => ({
  question: question,
  answer: '',
  title: 'Paste the HTS code from the Classify tool',
  context: "We'll check it's a real HTS code, then confirm AES accepts it for exports.",
  input: {
    label: 'HTS code',
    placeholder: 'For example 8806.23.0000',
    hint: '10 digits. With or without the dots is fine.',
    submit_label: 'Check this code',
  },
  options: [
    {
      id: 'handoff:open_classify',
      label: 'Open the Classify tool again',
      value: '',
      action: 'open_classify_tool',
    },
    {
      id: 'return:schedule_b',
      label: 'I have a Schedule B code instead',
      description: 'Type it in the box below.',
      value: '',
      action: 'focus_composer',
    },
  ],
});

const sanitizeExportMessagesForRecovery = (messages: IMessage[]): IMessage[] =>
  messages.filter((message) => typeof message.text === 'string');

const EXPORT_PHASE_ORDER: ExportPhaseKey[] = [
  'profile',
  'schedule_b',
  'country',
  'nrc',
  'itar',
  'ccl',
];

/** The current phase key for a phase / stage name, including pre-merge names; '' if unknown. */
const toExportPhaseKey = (value: unknown): ExportPhaseKey | '' => {
  const raw = String(value || '');
  const mapped = LEGACY_EXPORT_PHASE_ALIASES[raw] || raw;
  return EXPORT_PHASE_ORDER.includes(mapped as ExportPhaseKey)
    ? (mapped as ExportPhaseKey)
    : '';
};

const EXPORT_PHASE_LABEL: Record<ExportPhaseKey, string> = {
  profile: 'Product Profile & EEI Check',
  schedule_b: 'Schedule B / HTS Classification',
  country: 'Country and Control List Check',
  nrc: 'Nuclear (NRC + DOE)',
  itar: 'ITAR Check',
  ccl: 'EAR / CCL Check',
};

// Short heading shown in the chat's "Thought" panel before that phase's
// group/step lines — matches the labels in the right-rail Classification
// Progress panel (exportProgressSteps) so both sides name the phase the same
// way.
const EXPORT_PHASE_CHAT_HEADING: Record<ExportPhaseKey, string> = {
  profile: 'Product Profile',
  schedule_b: 'Schedule B / HTS Classification',
  country: 'Country and Control List Check',
  nrc: 'Nuclear (NRC + DOE)',
  itar: 'ITAR / USML Check',
  ccl: 'EAR / CCL Check',
};

// NOTE: profile uses the SAME key for phase status, stage, and data key (unlike
// country/country_screening) — keep all three identical. Its EEI determination
// additionally rides along under data.eei (like ccl ↔ ear_ccl).
const EXPORT_PHASE_STAGE: Record<ExportPhaseKey, EarStage> = {
  profile: 'profile',
  schedule_b: 'schedule_b',
  country: 'country_screening',
  nrc: 'nrc',
  itar: 'itar',
  ccl: 'ccl',
};

const EXPORT_PHASE_DATA_KEY: Record<ExportPhaseKey, string> = {
  profile: 'profile',
  schedule_b: 'schedule_b',
  country: 'country_screening',
  nrc: 'nrc',
  itar: 'itar',
  ccl: 'ear_ccl',
};

// Extra UI-data / assessment keys a phase owns beyond EXPORT_PHASE_DATA_KEY: the Country and
// Control List Check's control-list part keeps its pre-merge key.
const EXPORT_PHASE_PART_KEYS: Partial<Record<ExportPhaseKey, string[]>> = {
  country: ['export_screening'],
};

type WorkflowCommand =
  | { action: Exclude<WorkflowCommandAction, 'goto'> }
  | { action: 'goto'; target_phase: ExportPhaseKey };

const isEarStageValue = (
  stage: iStreamedResponse['stage'],
): stage is EarStage =>
  Boolean(stage && EAR_STAGE_ORDER.includes(stage as EarStage));

const formatThoughtDuration = (seconds: number | null): string => {
  if (!seconds || seconds < 2) {
    return 'Thought for a moment';
  }

  return `Thought for ${seconds}s`;
};

const toJsonSafe = <T,>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value ?? null));
  } catch (_error) {
    return value;
  }
};

const savedString = (value: any) =>
  typeof value === 'string' ? value.trim() : '';

const hasFollowupAnswer = (item: Partial<iFollowupItem>) =>
  typeof item.answer === 'string' ? Boolean(item.answer.trim()) : Boolean(item.answer);

const parseJsonObject = (value: any) => {
  if (value && typeof value === 'object') {
    return value;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const getSavedExportWorkflowData = (ticket: any) => {
  const raw = Array.isArray(ticket?.workflowData)
    ? ticket.workflowData[0]
    : ticket?.workflowData;

  return raw && typeof raw === 'object' ? raw : {};
};

const getSavedExportWorkflowState = (ticket: any, savedWorkflowData: any) =>
  parseJsonObject(
    savedWorkflowData?._workflowStateJson ||
      savedWorkflowData?.workflowStateJson ||
      ticket?.workflowState,
  );

const isPlainObject = (value: any) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasValue = (value: any) => value !== undefined && value !== null;

const mergePreservingKnownValues = (previous: any, incoming: any): any => {
  if (!isPlainObject(incoming)) {
    return hasValue(incoming) ? incoming : previous;
  }

  const base = isPlainObject(previous) ? { ...previous } : {};

  Object.entries(incoming).forEach(([key, value]) => {
    if (!hasValue(value)) {
      return;
    }

    base[key] = isPlainObject(value)
      ? mergePreservingKnownValues(base[key], value)
      : value;
  });

  return base;
};

const mergeWorkflowData = (previous: any, incoming: any) => {
  if (!isPlainObject(incoming)) {
    return previous || {};
  }

  const merged = mergePreservingKnownValues(previous || {}, incoming);
  // `phase_assessments` is the server's COMPLETE, authoritative map on every frame
  // (resilience plan Slice 6) — replace it wholesale rather than deep-merging, so a record
  // the server dropped on redo/reset/recompute cannot linger client-side (stale
  // not_assessed OR stale assessed).
  if (isPlainObject(incoming.phase_assessments)) {
    merged.phase_assessments = { ...incoming.phase_assessments };
  }
  // Same for `review_scope`: authoritative per frame, so an emptied `not_requested` (a
  // scoped-out phase re-added mid-run) replaces the old list instead of being skipped as blank.
  if (isPlainObject(incoming.review_scope)) {
    merged.review_scope = { ...incoming.review_scope };
  }
  return merged;
};

const STEP_STATUS_RANK: Record<string, number> = {
  pending: 0,
  running: 1,
  done: 2,
  skipped: 2,
  error: 3,
};

const mergeStepStatus = (previous: any, incoming: any) => {
  if (!isPlainObject(previous) || !isPlainObject(incoming)) {
    return hasValue(incoming) ? incoming : previous;
  }

  const previousStatus = previous.status;
  const incomingStatus = incoming.status;
  if (
    previousStatus in STEP_STATUS_RANK &&
    incomingStatus in STEP_STATUS_RANK &&
    STEP_STATUS_RANK[incomingStatus] < STEP_STATUS_RANK[previousStatus]
  ) {
    return previous;
  }

  return mergePreservingKnownValues(previous, incoming);
};

const mergeStepsStatus = (previous: any, incoming: any) => {
  if (!isPlainObject(incoming)) {
    return previous || {};
  }

  return Object.entries(incoming).reduce(
    (acc: any, [key, value]) => ({
      ...acc,
      [key]: mergeStepStatus(acc[key], value),
    }),
    { ...(previous || {}) },
  );
};

const mergeWorkflowState = (previous: any, incoming: any) => {
  const merged = mergePreservingKnownValues(previous || {}, incoming || {});

  if (
    isPlainObject(incoming?._control) &&
    Object.prototype.hasOwnProperty.call(incoming._control, 'pending_decision') &&
    incoming._control.pending_decision === null
  ) {
    merged._control = {
      ...(merged._control || {}),
      pending_decision: null,
    };
  }

  if (
    isPlainObject(incoming?._meta?.control) &&
    Object.prototype.hasOwnProperty.call(
      incoming._meta.control,
      'pending_decision',
    ) &&
    incoming._meta.control.pending_decision === null
  ) {
    merged._meta = {
      ...(merged._meta || {}),
      control: {
        ...(merged._meta?.control || {}),
        pending_decision: null,
      },
    };
  }

  const previousStepsStatus = previous?._meta?.steps_status;
  const incomingStepsStatus = incoming?._meta?.steps_status;
  if (previousStepsStatus || incomingStepsStatus) {
    merged._meta = {
      ...(merged._meta || {}),
      steps_status: mergeStepsStatus(previousStepsStatus, incomingStepsStatus),
    };
  }

  return merged;
};

const normalizeResetPhases = (value: any): ExportPhaseKey[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .map((phase) => toExportPhaseKey(phase))
        .filter((phase): phase is ExportPhaseKey => Boolean(phase)),
    ),
  );
};

const clearWorkflowDataForResetPhases = (
  previous: any,
  resetPhases: ExportPhaseKey[],
) => {
  if (!resetPhases.length) {
    return previous || {};
  }

  const next = { ...(previous || {}) };
  resetPhases.forEach((phase) => {
    if (phase === 'profile') {
      // The profile card renders BOTH payloads (like ccl ↔ ear_ccl + trace).
      delete next.profile;
      delete next.eei;
      return;
    }
    delete next[EXPORT_PHASE_DATA_KEY[phase]];
    (EXPORT_PHASE_PART_KEYS[phase] || []).forEach((key) => {
      delete next[key];
    });
  });
  // Drop the reset phases' assessment records too, so a re-run starts from a clean state.
  if (isPlainObject(next.phase_assessments)) {
    const assessments = { ...next.phase_assessments };
    resetPhases.forEach((phase) => {
      delete assessments[phase];
      (EXPORT_PHASE_PART_KEYS[phase] || []).forEach((key) => {
        delete assessments[key];
      });
    });
    next.phase_assessments = assessments;
  }

  return next;
};

const getActiveResetPhasesForIncoming = (
  activeResetPhases: ExportPhaseKey[],
  incomingPhaseStatus?: Partial<ExportPhaseStatus>,
  eventPhase?: ExportPhaseKey | '',
  event?: iStreamedResponse['event'],
) =>
  activeResetPhases.filter((phase) => {
    if (event === 'phase_completed' && eventPhase === phase) {
      return false;
    }

    return !['complete', 'skipped', 'error'].includes(
      incomingPhaseStatus?.[phase] || '',
    );
  });

const removeResolvedResetPhases = (
  activeResetPhases: ExportPhaseKey[],
  phaseStatus: ExportPhaseStatus,
) =>
  activeResetPhases.filter(
    (phase) => !['complete', 'skipped', 'error'].includes(phaseStatus[phase]),
  );

const stepBelongsToResetPhase = (stepKey: string, phase: ExportPhaseKey) => {
  if (phase === 'profile') {
    // profile.* plus the legacy step keys of the intake it absorbed (old saved
    // states may still carry them in _meta.steps_status). schedule_b.code_intake is
    // Schedule B / HTS's own step again (UX redesign slice B).
    return (
      stepKey.startsWith('profile.') ||
      stepKey.startsWith('eei.') ||
      ['schedule_b.product_intake', 'country.intake'].includes(stepKey)
    );
  }

  if (phase === 'country') {
    // Both parts of the Country and Control List Check.
    return stepKey.startsWith('country.') || stepKey.startsWith('export_screening.');
  }

  if (phase === 'nrc' || phase === 'itar') {
    return stepKey === phase;
  }

  return stepKey.startsWith(`${phase}.`);
};

const clearWorkflowStateForResetPhases = (
  previous: any,
  resetPhases: ExportPhaseKey[],
) => {
  if (!resetPhases.length || !isPlainObject(previous)) {
    return previous || {};
  }

  const next = {
    ...previous,
    _meta: isPlainObject(previous._meta) ? { ...previous._meta } : previous._meta,
    regimes: isPlainObject(previous.regimes) ? { ...previous.regimes } : previous.regimes,
  };

  resetPhases.forEach((phase) => {
    if (phase === 'profile') {
      // Prune the profile-owned slices; the backend keeps the intake under its
      // historical homes (schedule_b.product_intake, country.intake) and re-streams
      // whatever the smart reset preserved on the next chunk. (schedule_b.code_intake
      // belongs to the Schedule B / HTS phase since slice B.)
      delete next.profile;
      delete next.eei;
      if (isPlainObject(next.schedule_b)) {
        next.schedule_b = { ...next.schedule_b };
        delete next.schedule_b.product_intake;
      }
      if (isPlainObject(next.country)) {
        next.country = { ...next.country };
        delete next.country.intake;
      }
    } else if (phase === 'country') {
      delete next.country;
      delete next.export_screening;
    } else if (phase === 'nrc' || phase === 'itar') {
      if (isPlainObject(next.regimes)) {
        delete next.regimes[phase];
      }
    } else if (phase === 'ccl') {
      if (isPlainObject(next.regimes)) {
        delete next.regimes.ear_ccl;
        delete next.regimes.ear_ccl_trace;
      }
    } else {
      delete next[phase];
    }
  });

  const stepsStatus = next._meta?.steps_status;
  if (isPlainObject(stepsStatus)) {
    next._meta = {
      ...(next._meta || {}),
      steps_status: Object.fromEntries(
        Object.entries(stepsStatus).filter(
          ([stepKey]) =>
            !resetPhases.some((phase) =>
              stepBelongsToResetPhase(stepKey, phase),
            ),
        ),
      ),
    };
  }

  return next;
};

// Only the named phases' step statuses (`_meta.steps_status`) — every other slice is kept.
const clearStepStatusesForPhases = (
  previous: any,
  phases: ExportPhaseKey[],
) => {
  const stepsStatus = previous?._meta?.steps_status;
  if (!phases.length || !isPlainObject(stepsStatus)) {
    return previous;
  }

  return {
    ...previous,
    _meta: {
      ...previous._meta,
      steps_status: Object.fromEntries(
        Object.entries(stepsStatus).filter(
          ([stepKey]) =>
            !phases.some((phase) => stepBelongsToResetPhase(stepKey, phase)),
        ),
      ),
    },
  };
};

const clearPhaseStatusForResetPhases = (
  previous: ExportPhaseStatus,
  resetPhases: ExportPhaseKey[],
): ExportPhaseStatus =>
  resetPhases.reduce(
    (acc, phase) => ({
      ...acc,
      [phase]: 'pending',
    }),
    { ...previous },
  );

const PHASE_STATUS_RANK: Record<string, number> = {
  pending: 0,
  in_progress: 1,
  complete: 2,
  skipped: 2,
  error: 3,
};

const strongerPhaseStatus = (previous?: string, incoming?: string) => {
  if (!incoming) {
    return previous || 'pending';
  }

  if (!previous) {
    return incoming;
  }

  return (PHASE_STATUS_RANK[incoming] ?? 0) >=
    (PHASE_STATUS_RANK[previous] ?? 0)
    ? incoming
    : previous;
};

const getFirstExportScreeningObject = (value: any) => {
  if (Array.isArray(value)) {
    return value.find((item) => item && typeof item === 'object') || {};
  }

  return value && typeof value === 'object' ? value : {};
};

const hasExportScreeningSummaryFields = (value: any) => {
  const candidate = getFirstExportScreeningObject(value);
  return Boolean(
    candidate?.risk_level ||
      candidate?.summary ||
      candidate?.explanation ||
      candidate?.key_factors?.length,
  );
};

const hasExportScreeningResult = (exportScreening: any) => {
  if (!exportScreening) {
    return false;
  }

  const candidates = [
    exportScreening?.results,
    exportScreening?.result,
    exportScreening?.summary_result,
    exportScreening?.ai_summary,
    exportScreening?.aiSums,
    exportScreening?.threat_ui,
    exportScreening,
  ];

  return candidates.some(hasExportScreeningSummaryFields);
};

const inferPhaseStatusFromData = (workflowData: any) => ({
  schedule_b:
    workflowData?.schedule_b?.product_name ||
    workflowData?.schedule_b?.schedule_b_code
      ? 'complete'
      : undefined,
  // Country and Control List Check: complete only once BOTH parts have a result — the
  // destination screen AND the control-list screen (or the control-list part is not
  // applicable to an intangible transfer).
  country:
    workflowData?.country_screening?.normalized_country_name &&
    (hasExportScreeningResult(workflowData?.export_screening) ||
      workflowData?.export_screening?.not_applicable)
      ? 'complete'
      : undefined,
  // Gate on the real terminal artifact (EEI filing determination), NOT the mere
  // existence of the `profile` object — the server ships a non-null `profile` from the
  // first chunk, so `|| workflowData?.profile` inferred 'complete' during intake and
  // (via the higher-rank merge) overrode the server's correct 'in_progress'.
  profile: workflowData?.eei?.filing_determination ? 'complete' : undefined,
  nrc:
    hasValue(workflowData?.nrc?.controlled) ||
    workflowData?.nrc?.reason ||
    workflowData?.nrc?.notes
      ? 'complete'
      : undefined,
  itar:
    hasValue(workflowData?.itar?.controlled) ||
    workflowData?.itar?.usml_category ||
    workflowData?.itar?.reason
      ? 'complete'
      : undefined,
  ccl: workflowData?.ear_ccl?.classification_result ? 'complete' : undefined,
});

const mergePhaseStatuses = (
  previous: ExportPhaseStatus,
  incoming?: Partial<ExportPhaseStatus>,
  workflowData?: any,
): ExportPhaseStatus => {
  const inferred = inferPhaseStatusFromData(workflowData);

  return EXPORT_PHASE_ORDER.reduce((acc, phase) => {
    const current = previous?.[phase] || 'pending';
    const withIncoming = strongerPhaseStatus(current, incoming?.[phase]);
    acc[phase] = strongerPhaseStatus(
      withIncoming,
      inferred[phase as keyof typeof inferred],
    );
    return acc;
  }, {} as ExportPhaseStatus);
};

const EARChatUI: React.FC<any> = ({ userHasMonitor }) => {
  const history = useHistory();
  const location = useLocation<{
    resumeExportTicket?: any;
    seedProductDescription?: string;
    // Facts handed over by the Design Assistant's "Send to Export Workflow" button, already
    // mapped onto THIS workflow's fact names (see its handleSendToExportWorkflow).
    seedFacts?: Record<string, string | undefined>;
  }>();
  const logAppAction = useLogAppAction();
  const WORKFLOW_CREDIT_COST = 10; // charged via spendCredits when a run completes
  // Block starting a run when the team can't cover its charge (spendCredits, 10
  // credits on completion). Re-read whenever the workflow returns to its start screen.
  const {
    insufficient: insufficientCredits,
    remaining: remainingCredits,
    refresh: refreshCredits,
    isFreeWorkflowTrial: isFreeWorkflowTrial,
  } = useWorkflowCredits(WORKFLOW_CREDIT_COST, 'export');
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  // A finished run has been charged; re-read the balance when back at the start screen.
  useEffect(() => {
    if (messages.length === 0) {
      refreshCredits();
    }
  }, [messages.length, refreshCredits]);
  const workflowGridRef = useScrollWorkflowIntoView(messages.length > 0);
  const [input, setInput] = useState('');
  // Set when the team's one trial run of the Export Workflow has already been
  // spent (denial arrives as a workflow_error frame with data.reason ===
  // 'trial_limit_reached'). Replaces the chat/composer with TrialRunUsedPanel
  // for the rest of this mount -- see the "Gate AI workflows..." ticket.
  const [trialLimitReached, setTrialLimitReached] = useState(false);
  const [workflowState, setWorkflowState] = useState<any>({});
  const [originCntry, setOriginCntry] = useState('');
  const { cur_user, ticketDraft, setTicketDraft, resetTicketDraft } =
    useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();

  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const {
    streamData,
    isStreaming,
    connectWebSocket,
    closeWebSocket,
    clearStreamData,
  } = useWebSocketFetcher(apiUrl);
  const chatScrollRef = useWorkflowChatScroll(messages.length, isStreaming);
  // Mirrors `isStreaming` for timers that outlive the render they were scheduled in.
  const isStreamingLiveRef = useRef(isStreaming);
  isStreamingLiveRef.current = isStreaming;
  // A turn that ended leaves no live "Thinking…" placeholder behind (it is only ever a
  // stand-in while a response streams).
  useEffect(() => {
    if (isStreaming) {
      return;
    }
    setMessages((prev) =>
      prev.some((msg) => msg.text === 'Thinking...')
        ? prev.filter((msg) => msg.text !== 'Thinking...')
        : prev,
    );
  }, [isStreaming]);

  const [userProductDesc, setUserProductDesc] = useState<string>('');

  const [workflowData, setWorkflowData] = useState<any>({});
  const [ticketType, setTicketType] = useState<string>('');
  const [followupItems, setFollowupItems] = useState<iFollowupItem[]>([]);
  const [clfId, setClfId] = useState<string | null>(null);
  const [cmdtDesc, setCmdtDesc] = useState<string | null>(null);
  const [HTSCode, setHTSCode] = useState<string | null>(null);
  const [explanatoryNotes, setExplanatoryNotes] = useState<string | null>(null);
  const [sectionNotes, setSectionNotes] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<number>(1);
  const [isModalOpen, setModalOpen] = useState(false);
  const [scheduleBStatus, setScheduleBStatus] = useState('pending');
  const [countryScreeningStatus, setCountryScreeningStatus] =
    useState('pending');
  const [profileStatus, setProfileStatus] = useState('pending');
  const [nrcStatus, setNrcStatus] = useState('pending');
  const [itarStatus, setItarStatus] = useState('pending');
  const [earStatus, setEarStatus] = useState('pending');

  // "Push to Export Hub" (WS1 slice 2): send the completed workflow's results
  // into an Export Hub product (creating one if none matches).
  const [pushOpen, setPushOpen] = useState(false);
  // Product handed over by the Export Hub's "Open Export Workflow" link, so the
  // push dialog can aim the results back at it. Empty for a standalone run.
  const linkedProduct = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      productId: sp.get('productId') || '',
      name: sp.get('name') || '',
      sku: sp.get('sku') || '',
      databaseId: sp.get('db') || '',
    };
  }, [location.search]);
  const [pushDatabases, setPushDatabases] = useState<ExportDatabase[]>([]);
  const [pushDbId, setPushDbId] = useState('');
  const [pushName, setPushName] = useState('');
  const [pushSku, setPushSku] = useState('');
  const [pushLoadingDbs, setPushLoadingDbs] = useState(false);
  const [pushing, setPushing] = useState(false);
  /**
   * Whether this team has any Export Hub database to push into, resolved BEFORE
   * the button is clicked so it can be disabled rather than opening a dialog
   * whose only content is "No Export Hub databases found for this team".
   *
   * 'unknown' fails open — a lookup that errored must not block a legitimate
   * push, and the dialog still handles the empty case if it turns out to be one.
   */
  const [exportDbAvailability, setExportDbAvailability] = useState<
    'unknown' | 'none' | 'some'
  >('unknown');
  const [uiActivePhase, setUiActivePhase] = useState<ExportPhaseKey | ''>('');
  // Bumped by resetEarSession so "New Export" also resets the landing page's scope choice.
  const [landingKey, setLandingKey] = useState(0);
  const [savingTicket, setSavingTicket] = useState(false);
  const [crossLinks, setCrossLinks] = useState<string[]>([]);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [frequency, setFrequency] = useState('Once');
  const [ticketId, setTicketID] = useState('');
  const [insufficientCreditsOpen] = useState(false);
  const [workflowChargeKey, setWorkflowChargeKey] = useState('');
  const [chargedStages, setChargedStages] = useState<Record<string, boolean>>(
    {},
  );
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isEndWorkflowModalOpen, setIsEndWorkflowModalOpen] = useState(false);
  const [isPhaseRunningModalOpen, setIsPhaseRunningModalOpen] = useState(false);
  const [runningPhaseLabel, setRunningPhaseLabel] = useState('');
  const [attemptedPhaseLabel, setAttemptedPhaseLabel] = useState('');
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [disableLeaveWarning, setDisableLeaveWarning] = useState(false);
  const [isResumedTicket, setIsResumedTicket] = useState(false);
  const [lastSavedWorkflowSignature, setLastSavedWorkflowSignature] =
    useState('');

  const workflowDataRef = useRef<any>({});
  const workflowStateRef = useRef<any>({});
  const phaseStatusRef = useRef<ExportPhaseStatus>({
    profile: 'pending',
    schedule_b: 'pending',
    country: 'pending',
    nrc: 'pending',
    itar: 'pending',
    ccl: 'pending',
  });
  const activeResetKeyRef = useRef('');
  // The user's verbatim OPENING message, captured once from the backend echo (clf.product_desc).
  // Saved on the ticket so the Classification Result record can show it as the first entry — it is
  // not part of followup_items, and userProductDesc can be overwritten by option/command clicks.
  const initialRequestRef = useRef('');
  // The clf_id the captured opening message belongs to — so a NEW classification (new clf_id) in a
  // still-mounted component replaces the prior run's opening message instead of persisting it.
  const initialRequestClfIdRef = useRef('');
  const activeResetPhasesRef = useRef<ExportPhaseKey[]>([]);
  const sessionChargedStagesRef = useRef<Set<string>>(new Set()); // New in-memory guard
  const chargingStagesRef = useRef<Set<string>>(new Set());
  const precheckedStagesRef = useRef<Set<string>>(new Set());
  const blockedStagesRef = useRef<Set<string>>(new Set());
  const chargeErrorShownStagesRef = useRef<Set<string>>(new Set());
  const allowNavigationRef = useRef(false);
  const leaveSaveInProgressRef = useRef(false);
  const resumeHydratedRef = useRef(false);
  const seedHydratedRef = useRef(false);

  // useEffect(() => {
  //   loadCreditsSummary();
  // }, [cur_user?.selected_team?.id]);

  const leaveWarningStorageKey = useMemo(() => {
    const userId = cur_user?.basics?.id || 'anon';
    return `export-workflow-leave-warning-optout-${userId}`;
  }, [cur_user?.basics?.id]);

  useEffect(() => {
    const localOptOut = localStorage.getItem(leaveWarningStorageKey) === 'true';
    setDisableLeaveWarning(localOptOut);
  }, [leaveWarningStorageKey]);

  const hasUnsavedWorkflowWork = useMemo(() => {
    const hasDraftData = Object.keys(ticketDraft || {}).length > 0;
    const hasWorkflowData = Object.keys(workflowData || {}).length > 0;
    const hasWorkflowState = Object.keys(workflowState || {}).length > 0;

    return (
      messages.length > 0 ||
      Boolean(input.trim()) ||
      Boolean(userProductDesc) ||
      Boolean(clfId) ||
      Boolean(cmdtDesc) ||
      Boolean(HTSCode) ||
      Boolean(originCntry) ||
      Boolean(explanatoryNotes) ||
      Boolean(sectionNotes) ||
      followupItems.length > 0 ||
      crossLinks.length > 0 ||
      hasWorkflowData ||
      hasWorkflowState ||
      hasDraftData
    );
  }, [
    HTSCode,
    clfId,
    cmdtDesc,
    crossLinks.length,
    explanatoryNotes,
    followupItems.length,
    input,
    messages.length,
    originCntry,
    sectionNotes,
    ticketDraft,
    userProductDesc,
    workflowData,
    workflowState,
  ]);

  const isWorkflowSaved = Boolean(ticketId || ticketDraft?._id);

  const shouldWarnOnLeave =
    userHasMonitor &&
    !disableLeaveWarning &&
    hasUnsavedWorkflowWork &&
    !isWorkflowSaved;

  useEffect(() => {
    if (!shouldWarnOnLeave) {
      return undefined;
    }

    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [shouldWarnOnLeave]);

  const navigateToPendingLocation = useCallback(() => {
    if (!pendingLocation) {
      return;
    }

    history.push({
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    });
  }, [history, pendingLocation]);

  const continueToPendingLocation = useCallback(() => {
    allowNavigationRef.current = true;
    navigateToPendingLocation();
    window.setTimeout(() => {
      allowNavigationRef.current = false;
      setPendingLocation(null);
    }, 0);
  }, [navigateToPendingLocation]);

  const completePendingLeaveAfterSave = useCallback(() => {
    if (!leaveSaveInProgressRef.current) {
      return;
    }

    leaveSaveInProgressRef.current = false;
    resetEarSession();
    continueToPendingLocation();
  }, [continueToPendingLocation]);

  const cancelPendingLeaveSave = () => {
    if (!leaveSaveInProgressRef.current) {
      return;
    }

    leaveSaveInProgressRef.current = false;
    setPendingLocation(null);
  };

  const handleBlockedNavigation = (nextLocation: any) => {
    if (allowNavigationRef.current) {
      return true;
    }

    if (!shouldWarnOnLeave) {
      return true;
    }

    setPendingLocation(nextLocation);
    setIsLeaveModalOpen(true);
    return false;
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    resetEarSession();
    continueToPendingLocation();
  };

  const handleLeaveAndOptOut = () => {
    localStorage.setItem(leaveWarningStorageKey, 'true');
    setDisableLeaveWarning(true);
    handleLeaveWithoutSaving();
  };

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }
  // Helper function outside component
  const calculateIsComplete = (phase_status: ExportPhaseStatus) => {
    // A phase is "settled" when complete OR skipped — an intangible transfer
    // legitimately skips Schedule B (and the control-list part of the Country and
    // Control List Check), so requiring === 'complete' for every phase would never
    // show the final card.
    const settled = (s?: string) => s === 'complete' || s === 'skipped';
    return (
      settled(phase_status?.schedule_b) &&
      settled(phase_status?.country) &&
      settled(phase_status?.profile) &&
      settled(phase_status?.ccl) &&
      settled(phase_status?.itar) &&
      settled(phase_status?.nrc)
    );
  };

  const getCurrentPhaseStatus = () => ({
    schedule_b: scheduleBStatus,
    country: countryScreeningStatus,
    profile: profileStatus,
    ccl: earStatus,
    itar: itarStatus,
    nrc: nrcStatus,
  });

  const getWorkflowSaveSignature = (overrides: any = {}) =>
    JSON.stringify(
      toJsonSafe({
        workflowData: overrides.workflowData ?? workflowData ?? {},
        workflowState: overrides.workflowState ?? workflowState ?? {},
        phaseStatus: overrides.phaseStatus ?? getCurrentPhaseStatus(),
        productDescription: overrides.userProductDesc ?? userProductDesc ?? '',
        commodityDescription: overrides.cmdtDesc ?? cmdtDesc ?? '',
        htsCode: overrides.HTSCode ?? HTSCode ?? '',
        originCountry: overrides.originCntry ?? originCntry ?? '',
        explanatoryNotes: overrides.explanatoryNotes ?? explanatoryNotes ?? '',
        sectionNotes: overrides.sectionNotes ?? sectionNotes ?? '',
        followups: overrides.followupItems ?? followupItems ?? [],
        streamData: overrides.streamData ?? streamData ?? [],
        crossLinks: overrides.crossLinks ?? crossLinks ?? [],
        shareEmail: overrides.shareEmail ?? shareEmail ?? [],
        frequency: overrides.frequency ?? frequency,
      }),
    );

  const getCurrentWorkflowSaveSnapshot = () => {
    const workflowDataForSave = workflowDataRef.current || workflowData || {};
    const workflowStateForSave = workflowStateRef.current || workflowState || {};
    const phaseStatus = mergePhaseStatuses(
      phaseStatusRef.current || getCurrentPhaseStatus(),
      undefined,
      workflowDataForSave,
    );

    return {
      workflowData: workflowDataForSave,
      workflowState: workflowStateForSave,
      phaseStatus: phaseStatus,
    };
  };

  const getCurrentWorkflowSaveSignature = () =>
    getWorkflowSaveSignature(getCurrentWorkflowSaveSnapshot());

  // ---- Push to Export Hub (WS1 slice 2) ------------------------------------
  const pushLabelStyle: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
  };
  const pushInputStyle: React.CSSProperties = {
    height: '38px',
    boxSizing: 'border-box',
    padding: '0 10px',
    borderRadius: '8px',
    border: '1px solid var(--es-soft-border)',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary-color)',
    fontFamily: 'Inter, sans-serif',
    fontSize: '13px',
    outline: 'none',
  };

  const hasWorkflowResults =
    Object.keys(workflowData || {}).length > 0 ||
    Object.keys(workflowState || {}).length > 0;

  // Resolve database availability as soon as there is something to push, so the
  // Push button can render disabled. Only runs once results exist, so a run that
  // never finishes costs nothing.
  const pushGroupId = cur_user?.selected_team?.id;
  useEffect(() => {
    // Back to 'unknown' before every lookup. Without this, switching teams
    // leaves the PREVIOUS team's 'none' in place while the new request is in
    // flight, so the button stays disabled against a team that may well have
    // databases — and a request that never settles would leave it disabled
    // permanently, contradicting the fail-open intent of 'unknown'.
    setExportDbAvailability('unknown');
    if (!hasWorkflowResults || !pushGroupId) return undefined;
    let cancelled = false;
    ExportHubAPI.getDatabases(pushGroupId)
      .then((dbs: ExportDatabase[]) => {
        if (!cancelled) setExportDbAvailability(dbs.length ? 'some' : 'none');
      })
      .catch(() => {
        if (!cancelled) setExportDbAvailability('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, [hasWorkflowResults, pushGroupId]);

  const openPushToHub = async () => {
    // Arriving from a product's Export Control Regime section, the link carries
    // that product — prefill from it so the push lands back where it started.
    setPushName(
      linkedProduct.name ||
        workflowData?.schedule_b?.product_name ||
        userProductDesc ||
        '',
    );
    setPushSku(linkedProduct.sku);
    setPushOpen(true);
    const groupId = cur_user?.selected_team?.id;
    if (!groupId) return;
    setPushLoadingDbs(true);
    try {
      const dbs = await ExportHubAPI.getDatabases(groupId);
      setPushDatabases(dbs);
      // The linked database only wins if it's still one this team can see.
      const linkedDb =
        linkedProduct.databaseId &&
        dbs.some((d: ExportDatabase) => d.id === linkedProduct.databaseId)
          ? linkedProduct.databaseId
          : '';
      if (linkedDb) setPushDbId(linkedDb);
      else if (dbs.length) setPushDbId((prev) => prev || dbs[0].id);
    } catch (e) {
      enqueueSnackbar('Could not load Export Hub databases.', {
        variant: 'error',
      });
    } finally {
      setPushLoadingDbs(false);
    }
  };

  /**
   * The product this run came from, but only while the dialog still points at
   * it. Changing the database or the SKU is how you retarget, so either edit
   * drops the binding and the server resolves by SKU (or creates) as usual.
   */
  const boundProductId =
    linkedProduct.productId &&
    pushDbId === linkedProduct.databaseId &&
    pushSku.trim() === linkedProduct.sku
      ? linkedProduct.productId
      : undefined;

  const submitPushToHub = async () => {
    const groupId = cur_user?.selected_team?.id;
    if (!groupId || !pushDbId) return;
    // Merge the flattened workflowData under the raw workflowState (nested wins)
    // so the server mapper sees results regardless of which shape holds them.
    const snap = getCurrentWorkflowSaveSnapshot();
    const merged = {
      ...(snap.workflowData || {}),
      ...(snap.workflowState || {}),
    };
    setPushing(true);
    try {
      const res = await ExportHubAPI.ingestWorkflow({
        group_id: groupId,
        database_id: pushDbId,
        product_id: boundProductId,
        name: pushName.trim() || undefined,
        internal_sku: pushSku.trim() || undefined,
        source_case_id: ticketDraft?._id || undefined,
        // Names the run on the product's Export Control Regime section.
        source_case_name: ticketDraft?.ticketName || undefined,
        workflow_state: merged,
      });
      enqueueSnackbar(
        `Pushed to Export Hub — ${
          res.created ? 'created' : 'updated'
        } product (${res.compliance_status}).`,
        { variant: 'success' },
      );
      setPushOpen(false);
      history.push(
        `/export-hub/databases/${pushDbId}/products/${res.product_id}`,
      );
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Failed to push results to Export Hub.',
        { variant: 'error' },
      );
    } finally {
      setPushing(false);
    }
  };

  const normalizeActivePhase = (phase?: string | null): ExportPhaseKey | '' => {
    if (!phase) {
      return '';
    }

    const aliases: Record<string, ExportPhaseKey> = {
      schedule_b: 'schedule_b',
      country: 'country',
      country_screening: 'country',
      profile: 'profile',
      // Grace alias: tickets saved before the restructure persisted 'eei'.
      eei: 'profile',
      // Pre-merge key of the Country and Control List Check's control-list part.
      export_screening: 'country',
      nrc: 'nrc',
      itar: 'itar',
      ccl: 'ccl',
      ear_ccl: 'ccl',
    };

    return aliases[phase] || '';
  };

  const activePhase = normalizeActivePhase(
    uiActivePhase ||
      workflowState?._meta?.control?.active_phase ||
      workflowState?._meta?.active_phase ||
      workflowState?._control?.active_phase,
  );

  const isPhaseSelected = (phase: ExportPhaseKey, status: string) =>
    activePhase ? activePhase === phase : status === 'in_progress';

  const hasShownResultsRef = useRef(false);
  const shownPhaseResultsRef = useRef<Set<string>>(new Set());
  // Guards the completion credit spend against double-charging the same
  // run (the ccl-complete chunk can otherwise be processed more than once);
  // reset alongside hasShownResultsRef in resetEarSession ("start again").
  const spentExportWorkflowStepsRef = useRef<Set<number>>(new Set());
  // Root cause of the Schedule B "two thought blocks" bug: message ids were
  // `Date.now()`, which only has millisecond resolution. A PHASE_RESULT
  // message immediately followed by its WORKFLOW_CONTROL follow-up (created
  // synchronously, in the same tick) got the SAME id — confirmed by React's
  // own "Encountered two children with the same key" warning. The "attach
  // thought block" effect's `prev.map(msg => msg.id === targetId ? {...} :
  // msg)` then matched BOTH messages sharing that id and attached the
  // identical thought block to both. A monotonic counter guarantees every
  // live message gets a distinct id regardless of timing.
  const messageIdCounterRef = useRef(0);
  const nextMessageId = () => {
    messageIdCounterRef.current += 1;
    return Date.now() * 1000 + messageIdCounterRef.current;
  };
  const lastProgressKeyRef = useRef<string>('');
  const lastProcessedChunkIndexRef = useRef(0);

  // Accumulates the distinct backend `progress_message` values seen since the
  // current turn started, so they can be shown as a "Thought for Ns" toggle
  // (mirrors the Classify HTS chat's thinking panel) once the answer arrives.
  const thoughtStepsRef = useRef<EarThoughtStep[]>([]);
  // Sub-stage keys (backend `current_step`) already seen this turn. The FIRST
  // progress line bearing a given current_step is promoted to a `group` heading;
  // later lines sharing that key are `step` children. Reset with thoughtStepsRef.
  const seenStepKeysRef = useRef<Set<string>>(new Set());
  const thinkingStartedAtRef = useRef<number | null>(null);
  const [thinkingElapsedSeconds, setThinkingElapsedSeconds] = useState(0);
  // Bumped whenever thoughtStepsRef gains a new step, purely to force a
  // re-render so the live "Thinking" card's subtext reflects the latest step
  // immediately instead of waiting for the once-a-second elapsed-time tick.
  const [, setThoughtStepsVersion] = useState(0);
  // Latest rendered DOM node for each phase's "Thought" heading, so clicking a
  // settled phase in the right-rail Classification Progress panel can scroll
  // the chat to where that phase's heading is. Overwritten each render, so it
  // always points at the most recent occurrence if a phase reran.
  const phaseHeadingRefs = useRef<Partial<Record<ExportPhaseKey, HTMLDivElement | null>>>(
    {},
  );

  const getPhaseKeyForStage = (
    stage: iStreamedResponse['stage'],
  ): ExportPhaseKey | '' => {
    if (stage === 'country_screening' || stage === 'export_screening') {
      return 'country';
    }

    return normalizeActivePhase(stage || '');
  };

  const getPhaseStatusForStage = (
    stage: iStreamedResponse['stage'],
    phaseStatus: ExportPhaseStatus,
  ) => {
    switch (stage) {
      case 'schedule_b':
        return phaseStatus.schedule_b;
      case 'country_screening':
      case 'export_screening': // pre-merge frames of the control-list part
        return phaseStatus.country;
      case 'profile':
        return phaseStatus.profile;
      case 'nrc':
        return phaseStatus.nrc;
      case 'itar':
        return phaseStatus.itar;
      case 'ccl':
        return phaseStatus.ccl;
      default:
        return undefined;
    }
  };

  // const loadCreditsSummary = async () => {
  //   if (!cur_user?.selected_team?.id) {
  //     return null;
  //   }

  //   const response = await CreditsAPI.getCreditsSummary(
  //     cur_user.selected_team.id,
  //   );
  //   if (response?.success) {
  //     const latestCredits = Number(response.result?.remaining_credits ?? 0);
  //     setRemainingCredits(latestCredits);
  //     setCreditsLoaded(true);
  //     return latestCredits;
  //   }

  //   return null;
  // };

  // const openInsufficientCredits = (required: number) => {
  //   setRequiredCredits(required);
  //   setInsufficientCreditsOpen(true);
  // };

  // const ensureCredits = async (
  //   required: number,
  //   options: { refresh?: boolean; openModalOnInsufficient?: boolean } = {},
  // ) => {
  //   const { refresh = true, openModalOnInsufficient = true } = options;

  //   let availableCredits = remainingCredits;
  //   if (!creditsLoaded || refresh) {
  //     const latestCredits = await loadCreditsSummary();
  //     if (latestCredits === null) {
  //       return false;
  //     }
  //     availableCredits = latestCredits;
  //   }

  //   if (availableCredits < required) {
  //     if (openModalOnInsufficient) {
  //       openInsufficientCredits(required);
  //     }
  //     return false;
  //   }

  //   return true;
  // };

  // const loadChargedStages = (key: string) => {
  //   if (!key) {
  //     return {};
  //   }

  //   try {
  //     const raw = localStorage.getItem(key);
  //     const parsed = raw ? JSON.parse(raw) : {};
  //     return parsed && typeof parsed === 'object' ? parsed : {};
  //   } catch (_error) {
  //     return {};
  //   }
  // };

  // const persistChargedStage = (key: string, stage: string) => {
  //   const previous = loadChargedStages(key);
  //   const next = { ...previous, [stage]: true };
  //   localStorage.setItem(key, JSON.stringify(next));
  //   setChargedStages(next);
  // };

  // Charges one step (1-based, EXPORT_PHASE_ORDER position) as its phase newly
  // reaches 'complete', not on start -- an interrupted/errored run only pays
  // for the phases it finished. Fire-and-forget: a logging failure here
  // shouldn't block the user from seeing their completed workflow.
  // Run complete: bill every step not already billed live. A phase that settled as
  // 'skipped' (not applicable / out of scope) never fires its own charge, and a run's
  // total is fixed, so its step is billed here.
  const spendRemainingExportWorkflowSteps = () => {
    EXPORT_PHASE_ORDER.forEach((_, index) => {
      spendExportWorkflowStepCredit(index + 1);
    });
  };

  const spendExportWorkflowStepCredit = useCallback(async (step: number) => {
    const teamId = cur_user?.selected_team?.id;
    if (!teamId || spentExportWorkflowStepsRef.current.has(step)) return;
    spentExportWorkflowStepsRef.current.add(step);

    // The team's free trial run: nothing to charge.
    if (isFreeWorkflowTrial()) {
      try {
        logAppAction('export_workflow:completed');
      } catch (error) {
        console.error('[EAR] Failed to log workflow completion', error);
      }
      return;
    }

    try {
      const referenceId = ticketDraft?._id ? String(ticketDraft._id) : null;
      // The unified spend chooses trial only when it can cover the complete
      // charge, then falls back to paid credits without splitting one run.
      const response = await CreditsAPI.spendCredits({
        teamId: teamId,
        source: 'export_workflow',
        referenceId: referenceId,
        step: step,
        runId: getWorkflowRunId('export_workflow', teamId),
        userId: cur_user?.basics?.id || null,
        userName: [cur_user?.basics?.firstName, cur_user?.basics?.lastName].filter(Boolean).join(' ') || null,
        userEmail: cur_user?.basics?.email || null,
      });

      if (!response?.success) {
        console.error('[EAR] Failed to log credit spend', response);
      }
    } catch (error) {
      // Never surface this to the user -- the workflow itself already
      // completed successfully.
      console.error('[EAR] Failed to log credit spend', error);
    }
    try {
        logAppAction('export_workflow:completed');
      } catch (error) {
        console.error('[BO] Failed to log investigation completion', error);
      }
  }, [cur_user?.selected_team?.id, ticketDraft?._id, isFreeWorkflowTrial]);

  const resetEarSession = () => {
    closeWebSocket();
    clearStreamData();
    resetTicketDraft();
    setTicketID('');
    setMessages([]);
    setInput('');
    setCurrentStage(1);
    setWorkflowState({});
    setWorkflowData({});
    workflowStateRef.current = {};
    workflowDataRef.current = {};
    setUserProductDesc('');
    initialRequestRef.current = '';
    initialRequestClfIdRef.current = '';
    setTicketType('');
    setFollowupItems([]);
    setClfId(null);
    setCmdtDesc(null);
    setHTSCode('');
    setOriginCntry('');
    setExplanatoryNotes('');
    setSectionNotes('');
    setScheduleBStatus('pending');
    setCountryScreeningStatus('pending');
    setProfileStatus('pending');
    setNrcStatus('pending');
    setItarStatus('pending');
    setEarStatus('pending');
    setUiActivePhase('');
    setLandingKey((key) => key + 1);
    phaseStatusRef.current = {
      schedule_b: 'pending',
      country: 'pending',
      profile: 'pending',
      nrc: 'pending',
      itar: 'pending',
      ccl: 'pending',
    };
    setSavingTicket(false);
    setCrossLinks([]);
    setModalOpen(false);
    setNewTicketName('');
    setShareEmail([]);
    setFrequency('Once');
    hasShownResultsRef.current = false;
    shownPhaseResultsRef.current.clear();
    activeResetKeyRef.current = '';
    activeResetPhasesRef.current = [];
    lastProcessedChunkIndexRef.current = 0;
    setWorkflowChargeKey('');
    setChargedStages({});
    setIsResumedTicket(false);
    setLastSavedWorkflowSignature('');
    sessionChargedStagesRef.current.clear(); // Clear memory guard on reset
    chargingStagesRef.current.clear();
    precheckedStagesRef.current.clear();
    blockedStagesRef.current.clear();
    chargeErrorShownStagesRef.current.clear();
  };

  // User-initiated "New Export" / "New Session" / "End Workflow" — resetEarSession is also
  // called for programmatic resets (hydrating a ticket, leaving the page), which don't log.
  const startOver = () => {
    logAppAction('export_workflow:start_over');
    resetEarSession();
    // Only a deliberate fresh export re-opens billing; programmatic resets (hydrating a
    // ticket, leaving the page) must not, or already-billed steps would be billed again.
    spentExportWorkflowStepsRef.current.clear();
    clearWorkflowRunId('export_workflow', cur_user?.selected_team?.id);
  };

  const getSavedPhaseStatus = (savedWorkflowData: any) => {
    const scheduleB = savedWorkflowData?.schedule_b || {};
    const country = savedWorkflowData?.country_screening || {};
    const eei = savedWorkflowData?.eei || {};
    const exportScreening = savedWorkflowData?.export_screening || {};
    const nrc = savedWorkflowData?.nrc || {};
    const itar = savedWorkflowData?.itar || {};
    const ccl = savedWorkflowData?.ear_ccl || {};

    // Country and Control List Check = destination screen + control-list screen. The
    // control-list part counts as done with a result or when not applicable (intangible).
    const countryPartDone = Boolean(country?.normalized_country_name);
    const controlListPartDone = Boolean(
      exportScreening?.not_applicable ||
        exportScreening?.risk_level ||
        exportScreening?.summary ||
        exportScreening?.explanation ||
        exportScreening?.key_factors?.length ||
        exportScreening?.opensearch_hits,
    );

    const status: ExportPhaseStatus = {
      // A phase the workflow marked NOT-APPLICABLE (e.g. Schedule B on an intangible
      // transfer — no commodity code) is 'skipped', not 'pending', so it
      // renders "Skipped" and is view-only (handleGotoPhase blocks a goto on a
      // not-applicable skipped phase) instead of appearing runnable and resolving to a
      // confusing "no code / N/A".
      schedule_b: scheduleB?.not_applicable
        ? 'skipped'
        : scheduleB?.schedule_b_code
        ? 'complete'
        : 'pending',
      // normalized_country_name is the SCREENING output — set only when Country
      // Screening actually runs. input_destination_country is captured earlier, during
      // the Product Profile destination intake, so keying on it marked Country
      // "complete" and hydrated a premature, all-N/A Country card on a ticket saved
      // right after Schedule B. Gate on the real output only (matches the live
      // inferPhaseStatusFromData rule). A pre-merge ticket saved between the two old
      // phases (country screened, control lists not yet run) is in progress.
      country:
        countryPartDone && controlListPartDone
          ? 'complete'
          : countryPartDone
          ? 'in_progress'
          : 'pending',
      // Real artifact only — NOT Object.keys(profile).length (the persisted profile
      // slice is non-empty well before the phase completes, which showed a premature
      // checkmark on saved/resumed tickets).
      profile: eei?.filing_determination ? 'complete' : 'pending',
      nrc: nrc?.controlled || nrc?.reason || nrc?.notes ? 'complete' : 'pending',
      itar:
        itar?.controlled || itar?.usml_category || itar?.reason
          ? 'complete'
          : 'pending',
      ccl:
        ccl?.classification_result || ccl?.reasoning || ccl?.license_requirement
          ? 'complete'
          : 'pending',
    };
    // Scoped review: a phase the user didn't request never ran — 'skipped' (renders "Not in
    // this review"), not 'pending' (which would read as still to run). A goto re-adds it.
    EXPORT_PHASE_ORDER.forEach((phase) => {
      if (status[phase] !== 'complete' && isPhaseNotRequested(savedWorkflowData, phase)) {
        status[phase] = 'skipped';
      }
    });
    return status;
  };

  const getResumeControlPayload = (
    savedWorkflowState: any,
    phaseStatus: ExportPhaseStatus,
  ) => {
    const savedControl = savedWorkflowState?._control || {};
    // normalizeActivePhase maps legacy saved values (e.g. 'eei') onto the
    // current phase keys before the lookup.
    const savedActive = normalizeActivePhase(savedControl?.active_phase);
    const activePhase =
      EXPORT_PHASE_ORDER.find((phase) => phase === savedActive) ||
      EXPORT_PHASE_ORDER.find((phase) => phaseStatus[phase] === 'complete') ||
      'profile';
    const activeIndex = EXPORT_PHASE_ORDER.indexOf(activePhase);
    const nextPhase =
      EXPORT_PHASE_ORDER.slice(Math.max(activeIndex + 1, 0)).find(
        (phase) => phaseStatus[phase] !== 'complete',
      ) || null;

    if (!nextPhase) {
      return null;
    }

    return {
      run_mode: savedControl?.run_mode || 'step',
      active_phase: activePhase,
      next_phase: nextPhase,
      awaiting_decision: true,
      available_actions: ['next', 'run_all'],
      phase_order: EXPORT_PHASE_ORDER,
      phase_labels: EXPORT_PHASE_LABEL,
    };
  };

  const hydrateSavedExportTicket = (ticket: any) => {
    resetEarSession();

    // Bug 9: the stored chat + opening message are read off the snapshot, then dropped from the
    // workflow data kept in state (they are re-written fresh on the next save).
    const savedTranscript = savedExportChatTranscript(ticket);
    const savedOpening = savedExportOpeningMessage(ticket);
    const savedWorkflowData = { ...getSavedExportWorkflowData(ticket) };
    delete savedWorkflowData[EXPORT_CHAT_TRANSCRIPT_KEY];
    delete savedWorkflowData[EXPORT_INITIAL_REQUEST_KEY];
    const savedWorkflowState = getSavedExportWorkflowState(
      ticket,
      savedWorkflowData,
    );
    const commodity = ticket?.commodity || {};
    const savedFollowUps = Array.isArray(ticket?.followUps)
      ? ticket.followUps
      : [];
    const savedSharedWith = Array.isArray(ticket?.sharedWith)
      ? ticket.sharedWith
      : [];
    const savedCrossLinks = Array.isArray(ticket?.crossRulings)
      ? ticket.crossRulings
      : [];
    const nextDescription =
      savedOpening ||
      savedString(savedWorkflowData?.product?.description) ||
      savedString(commodity?.commodityName);
    if (savedOpening) {
      initialRequestRef.current = savedOpening;
      initialRequestClfIdRef.current = '';
    }
    const nextCommodityName =
      savedString(commodity?.commodityName) ||
      savedString(savedWorkflowData?.product?.name) ||
      nextDescription;
    const nextHts =
      savedString(commodity?.harmonizedScheduleCode) ||
      savedString(savedWorkflowData?.schedule_b?.schedule_b_code) ||
      savedString(savedWorkflowData?.export_screening?.hs_code);
    const nextCountry =
      savedString(commodity?.destinationCountry) ||
      savedString(savedWorkflowData?.country_screening?.normalized_country_name) ||
      savedString(savedWorkflowData?.country_screening?.input_destination_country) ||
      savedString(savedWorkflowData?.export_screening?.destination_country);
    const phaseStatus = getSavedPhaseStatus(savedWorkflowData);
    // A loaded ticket's finished phases were already billed (or predate step billing).
    EXPORT_PHASE_ORDER.forEach((phase, index) => {
      if (phaseStatus[phase] === 'complete') {
        spentExportWorkflowStepsRef.current.add(index + 1);
      }
    });
    const controlPayload = getResumeControlPayload(
      savedWorkflowState,
      phaseStatus,
    );

    setTicketDraft(ticket || {});
    setTicketID(ticket?._id || '');
    setIsResumedTicket(Boolean(ticket?._id));
    setWorkflowData(savedWorkflowData);
    setWorkflowState(savedWorkflowState);
    workflowDataRef.current = savedWorkflowData;
    workflowStateRef.current = savedWorkflowState;
    setUserProductDesc(nextDescription);
    setCmdtDesc(nextCommodityName);
    setHTSCode(nextHts);
    setOriginCntry(nextCountry);
    setExplanatoryNotes(savedString(ticket?.exNotes));
    setSectionNotes(savedString(ticket?.chapterNotes));
    setCrossLinks(savedCrossLinks);
    setFollowupItems(savedFollowUps);
    setShareEmail(savedSharedWith);
    setFrequency(savedString(ticket?.frequency) || 'Once');
    setLastSavedWorkflowSignature(
      getWorkflowSaveSignature({
        workflowData: savedWorkflowData,
        workflowState: savedWorkflowState,
        phaseStatus: phaseStatus,
        userProductDesc: nextDescription,
        cmdtDesc: nextCommodityName,
        HTSCode: nextHts,
        originCntry: nextCountry,
        explanatoryNotes: savedString(ticket?.exNotes),
        sectionNotes: savedString(ticket?.chapterNotes),
        followupItems: savedFollowUps,
        crossLinks: savedCrossLinks,
        shareEmail: savedSharedWith,
        frequency: savedString(ticket?.frequency) || 'Once',
      }),
    );
    setScheduleBStatus(phaseStatus.schedule_b);
    setCountryScreeningStatus(phaseStatus.country);
    setProfileStatus(phaseStatus.profile);
    setNrcStatus(phaseStatus.nrc);
    setItarStatus(phaseStatus.itar);
    setEarStatus(phaseStatus.ccl);
    phaseStatusRef.current = phaseStatus;
    setUiActivePhase(
      normalizeActivePhase(controlPayload?.active_phase || '') ||
        EXPORT_PHASE_ORDER.find((phase) => phaseStatus[phase] !== 'complete') ||
        '',
    );

    const now = Date.now();
    const hydratedMessages: IMessage[] = [];
    const cardIdBase = now + savedFollowUps.length * 2 + 10;

    // Bug 9: a ticket saved with its chat resumes with that chat exactly as it was (opening
    // message, cards with the clicked option, thinking panels). Result cards were stored without
    // their payloads — they render from the saved workflow data, as the rebuilt chat below does.
    savedTranscript.forEach((saved) => {
      const message = saved as IMessage;
      if (message.type === 'PHASE_RESULT') {
        if (message.stage) {
          shownPhaseResultsRef.current.add(message.stage);
        }
        hydratedMessages.push({
          ...message,
          payload: {
            workflowData: savedWorkflowData,
            workflowState: savedWorkflowState,
          },
        });
        return;
      }
      if (message.type === 'FINAL_RESULT') {
        hasShownResultsRef.current = true;
        hydratedMessages.push({
          ...message,
          payload: { workflowData: savedWorkflowData },
        });
        return;
      }
      hydratedMessages.push(message);
    });

    // Tickets saved without a chat: rebuild it from the opening message, the
    // question/answer pairs and one card per completed phase.
    if (savedTranscript.length === 0 && nextDescription) {
      hydratedMessages.push({
        id: now,
        sender: 'user',
        text: nextDescription,
      });
    }

    // The clarifying questions the workflow asked mid-conversation (and the
    // user's replies) aren't stored as chat bubbles anywhere — only as
    // question/answer pairs on the ticket's followUps — so without this the
    // resumed chat jumped straight from the opening message to the result
    // cards, silently dropping every back-and-forth in between.
    (savedTranscript.length === 0 ? savedFollowUps : []).forEach((item: any, index: number) => {
      const question = savedString(item?.question);
      const answer = savedString(item?.answer);
      if (!question) {
        return;
      }
      hydratedMessages.push({
        id: now + index * 2 + 1,
        sender: 'bot',
        text: question,
      });
      if (answer) {
        hydratedMessages.push({
          id: now + index * 2 + 2,
          sender: 'user',
          text: answer,
        });
      }
    });

    EXPORT_PHASE_ORDER.forEach((phase, index) => {
      if (savedTranscript.length > 0 || phaseStatus[phase] !== 'complete') {
        return;
      }

      hydratedMessages.push({
        id: cardIdBase + index,
        sender: 'bot',
        text: '',
        type: 'PHASE_RESULT',
        stage: EXPORT_PHASE_STAGE[phase],
        payload: {
          workflowData: savedWorkflowData,
          workflowState: savedWorkflowState,
        },
      });
      shownPhaseResultsRef.current.add(EXPORT_PHASE_STAGE[phase]);
    });

    // A stored chat that already ends on its "what next" control or the final summary keeps that
    // ending; otherwise (rebuilt chat, or saved mid-question) add the resume prompt as before.
    const lastBotMessageType = [...hydratedMessages]
      .reverse()
      .find((message) => message.sender === 'bot')?.type;
    const transcriptEndsSettled =
      savedTranscript.length > 0 &&
      (lastBotMessageType === 'WORKFLOW_CONTROL' ||
        lastBotMessageType === 'FINAL_RESULT');

    if (!transcriptEndsSettled && controlPayload) {
      const resumeActive = controlPayload.active_phase as ExportPhaseKey;
      // A pre-merge ticket saved mid-way through the Country and Control List Check (country
      // screened, control lists not yet run) resumes INSIDE that phase — never call it complete.
      const resumeText =
        phaseStatus[resumeActive] === 'in_progress'
          ? `${EXPORT_PHASE_LABEL[resumeActive]} is partly done. Click Continue to finish it, or jump straight to a specific check by selecting it in the panel on the right.`
          : `${EXPORT_PHASE_LABEL[resumeActive]} is complete. Click Continue to move on to ${
              EXPORT_PHASE_LABEL[controlPayload.next_phase as ExportPhaseKey]
            }, or jump straight to a specific check by selecting it in the panel on the right.`;
      hydratedMessages.push({
        id: cardIdBase + 100,
        sender: 'bot',
        text: resumeText,
        type: 'WORKFLOW_CONTROL',
        stage: EXPORT_PHASE_STAGE[controlPayload.active_phase as ExportPhaseKey],
        event: 'phase_paused',
        payload: { control: controlPayload },
      });
    } else if (!transcriptEndsSettled && calculateIsComplete(phaseStatus)) {
      hasShownResultsRef.current = true;
      hydratedMessages.push({
        id: cardIdBase + 100,
        sender: 'bot',
        text: '',
        type: 'FINAL_RESULT',
        payload: {
          workflowData: savedWorkflowData,
        },
      });
    }

    setMessages(hydratedMessages);
  };

  const hasPhaseResultData = (stage: string, dataToCheck: any) => {
    if (stage === 'nrc') {
      return Boolean(dataToCheck?.nrc);
    }

    return true;
  };

  // Mirrors the non-billing fields hydrateSavedExportTicket sets when
  // resuming a saved ticket -- this restores raw component state directly
  // (a captured client-side snapshot), not a server ticket payload.
  const applyRecoveredExportChatState = (
    state: ExportChatRecoverableState,
  ) => {
    setMessages(state.messages);
    setInput(state.input);
    setCurrentStage(state.currentStage);
    setWorkflowData(state.workflowData);
    setWorkflowState(state.workflowState);
    workflowDataRef.current = state.workflowData;
    workflowStateRef.current = state.workflowState;
    setUserProductDesc(state.userProductDesc);
    setTicketType(state.ticketType);
    setFollowupItems(state.followupItems);
    setClfId(state.clfId);
    setCmdtDesc(state.cmdtDesc);
    setHTSCode(state.HTSCode);
    setOriginCntry(state.originCntry);
    setExplanatoryNotes(state.explanatoryNotes);
    setSectionNotes(state.sectionNotes);
    setCrossLinks(state.crossLinks);
    setTicketID(state.ticketId);
    setNewTicketName(state.newTicketName);
    // A pre-merge draft tracked the control-list part (old "Sensitive Tech") separately:
    // the merged phase is only complete once that part settled too.
    const recoveredCountryStatus =
      state.exportScreeningStatus &&
      state.countryScreeningStatus === 'complete' &&
      !['complete', 'skipped'].includes(state.exportScreeningStatus)
        ? 'in_progress'
        : state.countryScreeningStatus;
    setScheduleBStatus(state.scheduleBStatus);
    setCountryScreeningStatus(recoveredCountryStatus);
    setProfileStatus(state.profileStatus);
    setNrcStatus(state.nrcStatus);
    setItarStatus(state.itarStatus);
    setEarStatus(state.earStatus);
    setUiActivePhase(toExportPhaseKey(state.uiActivePhase));
    if (state.initialRequest && !isExportCommandLabel(state.initialRequest)) {
      initialRequestRef.current = state.initialRequest;
      initialRequestClfIdRef.current = String(state.clfId || '');
    }
    phaseStatusRef.current = {
      profile: state.profileStatus,
      schedule_b: state.scheduleBStatus,
      country: recoveredCountryStatus,
      nrc: state.nrcStatus,
      itar: state.itarStatus,
      ccl: state.earStatus,
    };
  };

  // Keeps the recoverable registry pointed at the latest values so a forced
  // re-login redirect (see query-client.ts's handleAuthError) can snapshot
  // whatever's actually on screen at that moment. Written directly during
  // render rather than via a useEffect with a giant dependency array --
  // cheap, and always fresh by the time a capture could possibly happen.
  const exportRecoverableStateRef = useRef<ExportChatRecoverableState>(
    {} as ExportChatRecoverableState,
  );
  exportRecoverableStateRef.current = {
    messages: sanitizeExportMessagesForRecovery(messages),
    input: input,
    currentStage: currentStage,
    workflowState: workflowState,
    workflowData: workflowData,
    userProductDesc: userProductDesc,
    ticketType: ticketType,
    followupItems: followupItems,
    clfId: clfId,
    cmdtDesc: cmdtDesc,
    HTSCode: HTSCode,
    originCntry: originCntry,
    explanatoryNotes: explanatoryNotes,
    sectionNotes: sectionNotes,
    crossLinks: crossLinks,
    ticketId: ticketId,
    newTicketName: newTicketName,
    scheduleBStatus: scheduleBStatus,
    countryScreeningStatus: countryScreeningStatus,
    profileStatus: profileStatus,
    nrcStatus: nrcStatus,
    itarStatus: itarStatus,
    earStatus: earStatus,
    uiActivePhase: uiActivePhase,
    initialRequest: initialRequestRef.current,
  };

  useEffect(() => {
    return registerRecoverable(
      EXPORT_CHAT_RECOVERY_KEY,
      () => exportRecoverableStateRef.current,
    );
  }, []);

  useEffect(() => {
    const resumeTicket = location.state?.resumeExportTicket;

    if (
      resumeTicket?.docType === 'Export Unified' &&
      !resumeHydratedRef.current
    ) {
      resumeHydratedRef.current = true;
      clearExportClassifyHandoff();
      // A resumed saved ticket is a new billing run; its finished phases are marked billed
      // in hydrateSavedExportTicket, so only phases completed from here on are charged.
      clearWorkflowRunId('export_workflow', cur_user?.selected_team?.id);
      hydrateSavedExportTicket(resumeTicket);
      // The resume ticket is one-shot: drop it from this history entry, or browser Back /
      // refresh / a leave guard's re-push replays it later (PR review: Back from the Classify
      // tool reloaded the OLD saved ticket and wiped the current run + its handoff record).
      const { resumeExportTicket: _consumedTicket, ...restState } = (location.state ||
        {}) as Record<string, unknown>;
      allowNavigationRef.current = true; // this REPLACE must not trip the leave guard
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: Object.keys(restState).length ? restState : undefined,
      });
      window.setTimeout(() => {
        allowNavigationRef.current = false;
      }, 0);
      return;
    }

    // Back from the Classify tool (slice B): restore the exact session, then a "Welcome back"
    // divider and the paste card that answers the still-pending code question.
    const handoff = takeExportClassifyHandoff<ExportChatRecoverableState>();
    if (handoff?.snapshot) {
      applyRecoveredExportChatState(handoff.snapshot);
      const pendingQuestion = handoff.pendingQuestion;
      const stillPending = (handoff.snapshot.followupItems || []).some(
        (item) => item.question === pendingQuestion && !hasFollowupAnswer(item),
      );
      if (stillPending) {
        setMessages((prev) => [
          ...prev,
          { id: nextMessageId(), sender: 'bot', text: '', type: 'EXPORT_RETURN_DIVIDER' },
          {
            id: nextMessageId(),
            sender: 'bot',
            text: pendingQuestion,
            type: 'FOLLOWUP_CARD',
            payload: {
              item: buildClassifyReturnCardItem(pendingQuestion),
              returnCard: true,
              prefill: extractTenDigitCode(handoff.returnedCode) || '',
            },
          },
        ]);
      }
      return;
    }

    const recovered = loadDraft<ExportChatRecoverableState>(
      EXPORT_CHAT_RECOVERY_KEY,
    );
    if (recovered) {
      applyRecoveredExportChatState(recovered);
      return;
    }

    // Nothing to restore: a brand-new session, so it gets its own run id (any id left behind by
    // an abandoned run is dropped). The Classify round trip and recovery drafts returned above
    // and keep theirs.
    clearWorkflowRunId('export_workflow', cur_user?.selected_team?.id);
    spentExportWorkflowStepsRef.current.clear();

    // Opened from an Export Hub product ("Open Export Workflow"): start the landing page's
    // description with the product name. Read-only use of its query params; a Design Advisor
    // seed (below) outranks it.
    if (linkedProduct.name && !location.state?.seedProductDescription) {
      setInput((prev) => prev || linkedProduct.name);
    }
  }, []);

  // Handoff seed from the Export Design Advisor ("Send to Export Workflow"): pre-fill the
  // composer with the designed product's description so the user can review and send. The
  // forward workflow still re-classifies from scratch — this only saves re-typing. Runs once,
  // and never when resuming a saved ticket.
  useEffect(() => {
    const seed = location.state?.seedProductDescription;
    if (
      typeof seed === 'string' &&
      seed.trim() &&
      !seedHydratedRef.current &&
      !location.state?.resumeExportTicket
    ) {
      seedHydratedRef.current = true;
      setInput(seed.trim());

      // Carry over what the Design Assistant already established, instead of making the user
      // re-answer it here. Seeding the fact ledger (rather than only the composer text) means
      // the supervisor's targeted intake skips these instead of asking again — the same
      // mechanism hydrateSavedExportTicket uses to restore a saved ticket. The server
      // deep-merges whatever we send as workflow_state on the next turn.
      const seedFacts = location.state?.seedFacts;
      if (seedFacts) {
        const now = new Date().toISOString();
        const entries = Object.entries(seedFacts).filter(
          ([, value]) => typeof value === 'string' && value.trim(),
        );
        if (entries.length) {
          setWorkflowState((prev: any) => {
            const next = { ...(prev || {}) };
            const facts = { ...(next.facts || {}) };
            entries.forEach(([name, value]) => {
              // Never clobber something this workflow already knows — the user's own answers
              // here outrank a handed-over value.
              if (facts[name]) return;
              facts[name] = {
                value: (value as string).trim(),
                source: 'design_assistant',
                version: 1,
                updated_at: now,
              };
            });
            next.facts = facts;
            return next;
          });
        }
      }
    }
  }, []);

  // Ticks the "Thought for Ns" elapsed-time label while a response is in
  // flight. Mirrors the Classify HTS chat's thinking timer.
  useEffect(() => {
    if (!isStreaming) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setThinkingElapsedSeconds(
        thinkingStartedAtRef.current
          ? Math.max(
              0,
              Math.floor((Date.now() - thinkingStartedAtRef.current) / 1000),
            )
          : 0,
      );
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isStreaming]);

  // Process stream
  useEffect(() => {
    const newChunks = streamData.slice(lastProcessedChunkIndexRef.current);
    lastProcessedChunkIndexRef.current = streamData.length;

    newChunks.forEach(async (chunk) => {
      try {
        const classificationReq: iStreamedResponse = JSON.parse(chunk);
        const {
          message,
          progress_message,
          event,
          current_step,
          followup_items,
          product_desc,
          clf_id,
          explanatory_notes,
          section_chapter_notes,
          cross_data_links,
          data,
          phase_status,
          reset_phases,
          refresh_phases,
          workflow_state,
          control,
        } = classificationReq;
        // EW-2: a phase held as "skipped" that the server now runs (the control-list code
        // redirect re-admits Schedule B / HTS). Drop only its step + phase statuses so this
        // frame's values land despite the forward-only status merge; card data stays.
        const refreshPhases = normalizeResetPhases(refresh_phases);
        if (refreshPhases.length) {
          workflowStateRef.current = clearStepStatusesForPhases(
            workflowStateRef.current,
            refreshPhases,
          );
          phaseStatusRef.current = clearPhaseStatusForResetPhases(
            phaseStatusRef.current,
            refreshPhases,
          );
        }
        // Capture the verbatim opening message once PER classification. It never changes within a run,
        // but if a new classification begins (clf_id changes) while this component stays mounted, replace
        // it so the new ticket doesn't inherit the previous run's opening message.
        if (product_desc) {
          const requestClfId = String(clf_id || '');
          const isNewClassification =
            !!requestClfId && initialRequestClfIdRef.current !== requestClfId;
          if (!initialRequestRef.current || isNewClassification) {
            initialRequestRef.current = product_desc;
            initialRequestClfIdRef.current = requestClfId;
          }
        }
        const eventPhase = getPhaseKeyForStage(classificationReq.stage);
        const resetPhases = normalizeResetPhases(reset_phases);
        const resetKey = [...resetPhases].sort().join('|');
        const shouldApplyReset =
          Boolean(resetKey) && resetKey !== activeResetKeyRef.current;

        if (shouldApplyReset) {
          activeResetKeyRef.current = resetKey;
          activeResetPhasesRef.current = resetPhases;

          const resetWorkflowData = clearWorkflowDataForResetPhases(
            workflowDataRef.current,
            resetPhases,
          );
          const resetWorkflowState = clearWorkflowStateForResetPhases(
            workflowStateRef.current,
            resetPhases,
          );
          const resetPhaseStatus = clearPhaseStatusForResetPhases(
            phaseStatusRef.current,
            resetPhases,
          );
          const resetStages = new Set<string>(
            resetPhases.map((phase) => EXPORT_PHASE_STAGE[phase]),
          );
          // Pre-merge chats may still hold a separate control-list (export_screening)
          // result card — it belongs to the Country and Control List Check too.
          if (resetPhases.includes('country')) {
            resetStages.add('export_screening');
          }

          workflowDataRef.current = resetWorkflowData;
          workflowStateRef.current = resetWorkflowState;
          phaseStatusRef.current = resetPhaseStatus;
          hasShownResultsRef.current = false;
          resetStages.forEach((stage) => shownPhaseResultsRef.current.delete(stage));

          setWorkflowData(resetWorkflowData);
          setWorkflowState(resetWorkflowState);
          setScheduleBStatus(resetPhaseStatus.schedule_b);
          setCountryScreeningStatus(resetPhaseStatus.country);
          setProfileStatus(resetPhaseStatus.profile);
          setNrcStatus(resetPhaseStatus.nrc);
          setItarStatus(resetPhaseStatus.itar);
          setEarStatus(resetPhaseStatus.ccl);
          if (resetPhases.includes('schedule_b')) {
            setHTSCode('');
          }
          // Destination intake lives in the profile phase now — a profile redo
          // drops the stale right-rail country just like a country redo does.
          if (resetPhases.includes('country') || resetPhases.includes('profile')) {
            setOriginCntry('');
          }
          setUiActivePhase(resetPhases[0] || '');

          setMessages((prev) =>
            prev.filter((msg) => {
              if (msg.type === 'FINAL_RESULT' || msg.type === 'WORKFLOW_CONTROL') {
                return false;
              }

              return !(
                msg.type === 'PHASE_RESULT' &&
                isEarStageValue(msg.stage) &&
                resetStages.has(msg.stage)
              );
            }),
          );
        }

        const activeResetPhasesForIncoming = getActiveResetPhasesForIncoming(
          activeResetPhasesRef.current,
          phase_status,
          eventPhase,
          event,
        );
        const dataForMerge =
          data && activeResetPhasesForIncoming.length
            ? clearWorkflowDataForResetPhases(data, activeResetPhasesForIncoming)
            : data;
        const workflowStateForMerge =
          activeResetPhasesForIncoming.length && workflow_state
            ? clearWorkflowStateForResetPhases(
                workflow_state,
                activeResetPhasesForIncoming,
              )
            : workflow_state;

        // if (nextWorkflowChargeKey !== workflowChargeKey) {
        //   setWorkflowChargeKey(nextWorkflowChargeKey);
        //   setChargedStages(loadChargedStages(nextWorkflowChargeKey));
        // }

        // Phases that stream live sub-step progress (backend narration + heartbeat)
        // into the "Thinking" panel. Previously only 'ccl' surfaced step progress, so
        // schedule_b / nrc / itar sat on a plain "Thinking…" for the whole (often 30s)
        // turn with no narration at all. country_screening emits its own tool-progress
        // lines + oneshot heartbeats too (both parts of the Country and Control List Check
        // stream on it), so it belongs here as well. export_screening stays listed for
        // frames from pre-merge sessions.
        const STEP_PROGRESS_STAGES = [
          'ccl',
          'schedule_b',
          'nrc',
          'itar',
          'profile',
          'country_screening',
          'export_screening',
        ];
        const isRenderableProgressEvent =
          event === 'phase_started' ||
          event === 'phase_completed' ||
          event === 'workflow_error' ||
          event === 'cancelled' ||
          ((event === 'step_started' ||
            event === 'step_completed' ||
            event === 'step_finding') &&
            STEP_PROGRESS_STAGES.includes(classificationReq.stage ?? ''));

        // Progress text now arrives in `progress_message` (step/phase-start chatter);
        // phase_completed/workflow_error/cancelled still carry their text in `message`.
        // This narration no longer renders as its own chat bubble — it's folded into
        // the "Thinking" panel (see the `msg.text === 'Thinking...'` render branch)
        // so the same text isn't shown twice.
        const progressText = progress_message ?? message;
        if (isRenderableProgressEvent && progressText) {
          const trimmedProgressText = progressText.trim();
          const lastThoughtStep =
            thoughtStepsRef.current[thoughtStepsRef.current.length - 1];
          if (
            trimmedProgressText &&
            lastThoughtStep?.text !== trimmedProgressText
          ) {
            // Tag the line's level in the phase → group → step hierarchy.
            // - phase_started → `phase` (the per-phase overview / overall goal)
            // - step_finding → `finding` (a DETERMINATION — the result of a step)
            // - first line of a new sub-stage (distinct current_step) → `group`
            // - anything else → `step` (a fine-grained line under the group)
            // A finding is checked BEFORE the group rule so it never consumes the
            // group slot for its current_step (the next real step line still groups).
            let level: EarThoughtStep['level'] = 'step';
            if (event === 'phase_started') {
              level = 'phase';
            } else if (event === 'step_finding') {
              level = 'finding';
            } else if (
              current_step &&
              !seenStepKeysRef.current.has(current_step)
            ) {
              seenStepKeysRef.current.add(current_step);
              level = 'group';
            }
            thoughtStepsRef.current = [
              ...thoughtStepsRef.current,
              {
                text: trimmedProgressText,
                level: level,
                ...(level === 'phase' && eventPhase
                  ? { phaseKey: eventPhase }
                  : {}),
              },
            ];
            setThoughtStepsVersion((version) => version + 1);
          }

          // Errors/cancellations are terminal, not routine narration — surface
          // them as a real message instead of only inside the "Thinking" panel.
          if (event === 'workflow_error' && data?.reason === 'trial_limit_reached') {
            // Replaces the whole chat/composer area with TrialRunUsedPanel
            // instead of a chat bubble -- see the panel's render branch below.
            setTrialLimitReached(true);
          } else if (event === 'workflow_error' || event === 'cancelled') {
            const terminalKey = `TERMINAL:${event}:${
              classificationReq.stage || ''
            }:${message}`;

            if (terminalKey !== lastProgressKeyRef.current) {
              lastProgressKeyRef.current = terminalKey;

              const htmlMsg = progressText.replace(/\n/g, '<br/>');
              setMessages((prev) => [
                ...prev.filter((m) => m.text !== 'Thinking...'),
                {
                  id: nextMessageId(),
                  sender: 'bot',
                  text: htmlMsg,
                  type:
                    event === 'workflow_error'
                      ? 'WORKFLOW_ERROR'
                      : 'WORKFLOW_CANCELLED',
                  stage: classificationReq.stage,
                  event: event,
                },
              ]);
            }
          }
        }

        if (workflowStateForMerge) {
          const nextWorkflowState = mergeWorkflowState(
            workflowStateRef.current,
            workflowStateForMerge,
          );
          workflowStateRef.current = nextWorkflowState;
          setWorkflowState(nextWorkflowState);
        } else if (control?.active_phase) {
          const nextWorkflowState = {
            ...(workflowStateRef.current || {}),
            _meta: {
              ...(workflowStateRef.current?._meta || {}),
              control: {
                ...(workflowStateRef.current?._meta?.control || {}),
                ...control,
              },
            },
          };
          workflowStateRef.current = nextWorkflowState;
          setWorkflowState(nextWorkflowState);
        }

        if (
          eventPhase &&
          (event === 'phase_started' || event === 'phase_completed')
        ) {
          setUiActivePhase(eventPhase);
          const nextWorkflowState = mergeWorkflowState(
            workflowStateRef.current,
            {
              _meta: { control: { active_phase: eventPhase } },
              _control: { active_phase: eventPhase },
            },
          );
          workflowStateRef.current = nextWorkflowState;
          setWorkflowState(nextWorkflowState);
        }

        if (
          event === 'phase_paused' &&
          message &&
          control?.awaiting_decision
        ) {
          const pausedPhase = normalizeActivePhase(control.active_phase || '');
          if (pausedPhase) {
            setUiActivePhase(pausedPhase);
          }

          const controlKey = `WORKFLOW_CONTROL:${control.active_phase || ''}:${
            control.next_phase || ''
          }:${message}`;

          if (controlKey !== lastProgressKeyRef.current) {
            lastProgressKeyRef.current = controlKey;

            setMessages((prev) => [
              ...prev.filter((msg) => msg.text !== 'Thinking...'),
              {
                id: nextMessageId(),
                sender: 'bot',
                text: message,
                type: 'WORKFLOW_CONTROL',
                payload: { control: control },
              },
            ]);
          }
        }

        // Keep the progress-panel highlight in sync with the backend's active phase.
        // The backend reports control.active_phase on every chunk, but the highlight
        // (uiActivePhase) was only refreshed on phase_started/phase_completed/
        // phase_paused — so routing into a phase that ends on a followup (e.g. a
        // regime's prerequisite question after "go to NRC") left the optimistic
        // highlight stale on the originally-clicked card.
        // Skip while a typed decision (e.g. the order-of-review advisory) is pending,
        // so the card the user just clicked keeps its optimistic highlight until they
        // actually choose.
        if (control?.active_phase && !control?.flags?.pending_decision) {
          const backendActivePhase = normalizeActivePhase(control.active_phase);
          if (backendActivePhase) {
            setUiActivePhase(backendActivePhase);
          }
        }

        const incomingWorkflowData = dataForMerge
          ? mergeWorkflowData(workflowDataRef.current, dataForMerge)
          : workflowDataRef.current;
        const currentPhaseStatus = phaseStatusRef.current;
        const mergedPhaseStatus = mergePhaseStatuses(
          currentPhaseStatus,
          phase_status,
          incomingWorkflowData,
        );

        if (phase_status || dataForMerge) {
          // One record per phase that newly reaches 'complete'. A phase that
          // settles as 'skipped' (not applicable / not in scope / jumped past)
          // never gets here, and hydrated or replayed phases are already
          // 'complete' in the ref so they don't re-log.
          EXPORT_PHASE_ORDER.forEach((phase) => {
            if (
              mergedPhaseStatus[phase] === 'complete' &&
              currentPhaseStatus?.[phase] !== 'complete'
            ) {
              logAppAction(`export_workflow:${phase}_completed`);
              spendExportWorkflowStepCredit(
                EXPORT_PHASE_ORDER.indexOf(phase) + 1,
              );
            }
          });
          phaseStatusRef.current = mergedPhaseStatus;
          setScheduleBStatus(mergedPhaseStatus.schedule_b || 'pending');
          setCountryScreeningStatus(mergedPhaseStatus.country || 'pending');
          setProfileStatus(mergedPhaseStatus.profile || 'pending');
          setNrcStatus(mergedPhaseStatus.nrc || 'pending');
          setItarStatus(mergedPhaseStatus.itar || 'pending');
          setEarStatus(mergedPhaseStatus.ccl || 'pending');

          if (activeResetPhasesRef.current.length) {
            activeResetPhasesRef.current = removeResolvedResetPhases(
              activeResetPhasesRef.current,
              mergedPhaseStatus,
            );
          }

          if (!activeResetPhasesRef.current.length) {
            activeResetKeyRef.current = '';
          }

          setMessages((prev) =>
            prev.filter((streamMessage) => {
              // Once a phase is complete, drop its transient chrome: the "underway"
              // banner (PHASE_PROGRESS/phase_started) AND its live sub-step lines
              // (STEP_PROGRESS, the "doing X now" narration/heartbeat). The persistent
              // phase RESULT is a separate PHASE_RESULT message and is untouched.
              const isPhaseBanner =
                streamMessage.type === 'PHASE_PROGRESS' &&
                streamMessage.event === 'phase_started';
              const isStepProgress = streamMessage.type === 'STEP_PROGRESS';
              if (!isPhaseBanner && !isStepProgress) {
                return true;
              }

              const stageStatus = getPhaseStatusForStage(
                streamMessage.stage,
                mergedPhaseStatus,
              );

              return stageStatus !== 'complete';
            }),
          );

          const eventStage = classificationReq.stage as
            | (typeof EAR_STAGE_ORDER)[number]
            | undefined;
          const isEarStage = eventStage
            ? EAR_STAGE_ORDER.includes(eventStage)
            : false;

          // if (event === 'phase_started' && isEarStage && eventStage) {
          //   if (
          //     !precheckedStagesRef.current.has(eventStage) &&
          //     !blockedStagesRef.current.has(eventStage)
          //   ) {
          //     const hasCreditsForStage = await ensureCredits(STEP_CREDIT_COST, {
          //       refresh: true,
          //     });

          //     if (hasCreditsForStage) {
          //       precheckedStagesRef.current.add(eventStage);
          //     } else {
          //       blockedStagesRef.current.add(eventStage);
          //     }
          //   }
          // }

          if (event === 'phase_completed' && isEarStage && eventStage) {
            const nextWorkflowData = incomingWorkflowData;
            workflowDataRef.current = nextWorkflowData;

            if (
              !shownPhaseResultsRef.current.has(eventStage) &&
              hasPhaseResultData(eventStage, nextWorkflowData)
            ) {
              shownPhaseResultsRef.current.add(eventStage);

              setMessages((prev) => {
                const withoutThinking = prev.filter(
                  (msg) => msg.text !== 'Thinking...',
                );

                return [
                  ...withoutThinking,
                  {
                    id: nextMessageId(),
                    sender: 'bot',
                    text: '',
                    type: 'PHASE_RESULT',
                    stage: eventStage,
                    payload: {
                      workflowData: nextWorkflowData,
                      workflowState:
                        workflowStateForMerge || workflowStateRef.current,
                    },
                  },
                ];
              });
            }

            // // Check in-memory session FIRST to prevent shifting key double charges
            // const alreadyCharged =
            //   sessionChargedStagesRef.current.has(eventStage) ||
            //   Boolean(loadChargedStages(nextWorkflowChargeKey)[eventStage]);

            // if (
            //   alreadyCharged ||
            //   blockedStagesRef.current.has(eventStage) ||
            //   chargingStagesRef.current.has(eventStage)
            // ) {
            //   return;
            // }

            // // Lock synchronously before any async operations
            // chargingStagesRef.current.add(eventStage);

            // try {
            //   if (!precheckedStagesRef.current.has(eventStage)) {
            //     const hasCreditsForStage = await ensureCredits(
            //       STEP_CREDIT_COST,
            //       {
            //         refresh: true,
            //       },
            //     );

            //     if (!hasCreditsForStage) {
            //       blockedStagesRef.current.add(eventStage);
            //       return;
            //     }

            //     precheckedStagesRef.current.add(eventStage);
            //   }

            //   const spendResp = await CreditsAPI.spendCredits({
            //     teamId: cur_user.selected_team.id,
            //     cost: STEP_CREDIT_COST,
            //   });

            //   if (spendResp?.success) {
            //     setRemainingCredits((prev) =>
            //       Math.max(0, prev - STEP_CREDIT_COST),
            //     );

            //     // Save to in-memory guard
            //     sessionChargedStagesRef.current.add(eventStage);
            //     persistChargedStage(nextWorkflowChargeKey, eventStage);
            //     chargeErrorShownStagesRef.current.delete(eventStage);
            //   } else {
            //     const stillHasCredits = await ensureCredits(STEP_CREDIT_COST, {
            //       refresh: true,
            //       openModalOnInsufficient: false,
            //     });

            //     if (!stillHasCredits) {
            //       blockedStagesRef.current.add(eventStage);
            //       openInsufficientCredits(STEP_CREDIT_COST);
            //     } else if (!chargeErrorShownStagesRef.current.has(eventStage)) {
            //       chargeErrorShownStagesRef.current.add(eventStage);
            //       enqueueSnackbar(
            //         'Unable to charge credits for this workflow step. Please try again.',
            //         {
            //           variant: 'error',
            //         },
            //       );
            //     }
            //   }
            // } catch (error) {
            //   console.error('Error during billing step:', error);
            // } finally {
            //   // Ensure lock is released safely
            //   chargingStagesRef.current.delete(eventStage);
            // }
          }
        }

        if (dataForMerge) {
          const nextWorkflowData = incomingWorkflowData;
          workflowDataRef.current = nextWorkflowData;
          setWorkflowData(nextWorkflowData);
        }

        if (
          dataForMerge?.nrc &&
          mergedPhaseStatus.nrc === 'complete' &&
          !shownPhaseResultsRef.current.has('nrc')
        ) {
          shownPhaseResultsRef.current.add('nrc');

          setMessages((prev) => [
            ...prev.filter((msg) => msg.text !== 'Thinking...'),
            {
              id: nextMessageId(),
              sender: 'bot',
              text: '',
              type: 'PHASE_RESULT',
              stage: 'nrc',
              payload: {
                workflowData: mergeWorkflowData(
                  workflowDataRef.current,
                  dataForMerge,
                ),
                workflowState: workflowStateForMerge || workflowStateRef.current,
              },
            },
          ]);
        }

        if (
          dataForMerge?.itar &&
          mergedPhaseStatus.itar === 'complete' &&
          !shownPhaseResultsRef.current.has('itar')
        ) {
          shownPhaseResultsRef.current.add('itar');

          setMessages((prev) => [
            ...prev.filter((msg) => msg.text !== 'Thinking...'),
            {
              id: nextMessageId(),
              sender: 'bot',
              text: '',
              type: 'PHASE_RESULT',
              stage: 'itar',
              payload: {
                workflowData: mergeWorkflowData(
                  workflowDataRef.current,
                  dataForMerge,
                ),
                workflowState: workflowStateForMerge || workflowStateRef.current,
              },
            },
          ]);
        }

        if (
          phase_status?.ccl === 'complete' &&
          mergedPhaseStatus.ccl === 'complete' &&
          !shownPhaseResultsRef.current.has('ccl')
        ) {
          shownPhaseResultsRef.current.add('ccl');

          setMessages((prev) => [
            ...prev.filter((msg) => msg.text !== 'Thinking...'),
            {
              id: nextMessageId(),
              sender: 'bot',
              text: '',
              type: 'PHASE_RESULT',
              stage: 'ccl',
              payload: {
                workflowData: incomingWorkflowData,
                workflowState: workflowStateForMerge || workflowStateRef.current,
              },
            },
          ]);
        }

        if (dataForMerge?.product?.description) {
          setCmdtDesc(dataForMerge?.product?.description);
        }
        if (explanatory_notes) {
          setExplanatoryNotes(explanatory_notes);
        }
        if (section_chapter_notes) {
          setSectionNotes(section_chapter_notes);
        }
        if (cross_data_links) {
          setCrossLinks(cross_data_links);
        }

        const scheduleBCode = dataForMerge?.schedule_b?.schedule_b_code;
        if (scheduleBCode) {
          setHTSCode(scheduleBCode);
        }

        const country = dataForMerge?.country_screening?.normalized_country_name;
        if (country) {
          setOriginCntry(country);
        }

        // Use helper function
        const isComplete = calculateIsComplete(mergedPhaseStatus);
        // The summary card waits for the server's `final` frame (every finished run ends with one).
        // "All phases settled" alone fires too early: the Country and Control List Check reads as
        // complete once its control-list result lands, while its last step (Appendix X) still runs
        // and its own result card (phase_completed) follows ~1 s later — so the summary was added
        // above that card (EW-2).
        const isRunFinished =
          event === 'final' &&
          (isComplete || (Boolean(phase_status) && calculateIsComplete(phase_status)));

        if (
          !isComplete &&
          event === 'phase_completed' &&
          eventPhase === 'ccl'
        ) {
          spendRemainingExportWorkflowSteps();
          setMessages((prev) => [
            ...prev.filter(
              (msg) =>
                msg.text !== 'Thinking...' &&
                !(
                  msg.type === 'PHASE_PROGRESS' &&
                  msg.event === 'phase_completed' &&
                  getPhaseKeyForStage(msg.stage) === 'ccl'
                ) &&
                !(
                  msg.type === 'WORKFLOW_CONTROL' &&
                  normalizeActivePhase(
                    msg.payload?.control?.active_phase || '',
                  ) === 'ccl'
                ),
            ),
            {
              id: nextMessageId(),
              sender: 'bot',
              text:
                message ||
                'EAR / CCL Check is complete. You can end or save this workflow.',
              type: 'WORKFLOW_CONTROL',
              stage: 'ccl',
              event: 'phase_paused',
              payload: {
                control: {
                  ...(control || {}),
                  active_phase: 'ccl',
                  next_phase: null,
                  awaiting_decision: true,
                  available_actions: [],
                },
              },
            },
          ]);

        }

        if (isRunFinished && !hasShownResultsRef.current) {
          hasShownResultsRef.current = true;
          // A full run (every phase settled) ends here, never in the
          // !isComplete branch above, so it has to be settled here too.
          spendRemainingExportWorkflowSteps();
          // The description is NOT cleared here any more (Bug 9): it is the opening message the
          // ticket saves and every later turn (redo / goto) sends as product_desc.

          setMessages((prev) =>
            prev.filter((msg) => msg.text !== 'Thinking...'),
          );

          setTimeout(() => {
            setMessages((prev) => [
              ...prev,
              {
                id: nextMessageId(),
                sender: 'bot',
                text: '',
                type: 'FINAL_RESULT',
                payload: {
                  workflowData: incomingWorkflowData,
                },
              },
            ]);
          }, 400);
        }

        if (clf_id && clf_id.trim() !== '') {
          setClfId((prev) => (prev === clf_id ? prev : clf_id));
        }
        if (followup_items && followup_items.length > 0) {
          setFollowupItems((prev) => {
            const pendingQuestions = new Set(
              prev
                .filter((item) => !hasFollowupAnswer(item))
                .map((item) => item.question),
            );
            const incomingQuestions = new Set<string>();
            const newItems = followup_items.filter((item) => {
              if (hasFollowupAnswer(item) || !item.question) {
                return false;
              }

              if (
                pendingQuestions.has(item.question) ||
                incomingQuestions.has(item.question)
              ) {
                return false;
              }

              incomingQuestions.add(item.question);
              return true;
            });
            if (newItems.length > 0) {
              setMessages((previousMessages) =>
                previousMessages.filter((msg) => msg.text !== 'Thinking...'),
              );

              if (
                event === 'followup' &&
                classificationReq.stage === 'schedule_b' &&
                current_step === 'schedule_b.code_intake'
              ) {
                setTimeout(() => {
                  setMessages((previousMessages) =>
                    previousMessages.filter((msg) => msg.text !== 'Thinking...'),
                  );
                }, 900);
              }

              // One message per question: an option card when the server sent options /
              // an input (FollowupQuestionCard), else the plain-text bubble. (`text` stays
              // the plain question so recovery drafts keep the card.)
              setMessages((prevMsgs) => [
                ...prevMsgs,
                ...newItems.map(
                  (item): IMessage =>
                    isCardFollowup(item)
                      ? {
                          id: nextMessageId(),
                          sender: 'bot',
                          text: item.question,
                          type: 'FOLLOWUP_CARD',
                          payload: { item: item },
                        }
                      : {
                          id: nextMessageId(),
                          sender: 'bot',
                          text: item.question,
                          options: [],
                        },
                ),
              ]);
            } else if (event === 'followup') {
              // The server re-presented an already-pending question (e.g. resuming a
              // phase in progress after a jump) — no new bubble to add, but the
              // "Thinking..." spinner must still clear or it hangs indefinitely.
              setMessages((previousMessages) =>
                previousMessages.filter((msg) => msg.text !== 'Thinking...'),
              );
            }
            return [...prev, ...newItems];
          });
        }
      } catch (err) {
        console.warn('Invalid JSON chunk in useEffect:', chunk, err);
      }
    });
  }, [
    streamData,
    workflowChargeKey,
    chargedStages,
    cur_user.selected_team?.id,
  ]);

  // Once a turn's answer lands (any bot message that isn't the "Thinking..."
  // placeholder or transient progress chatter), attach the progress_message
  // steps collected during that turn as a collapsible "Thought for Ns" toggle,
  // then reset the accumulator for the next turn.
  useEffect(() => {
    if (thoughtStepsRef.current.length === 0) {
      return;
    }

    // The message its "Thought for Ns" block belongs to is not always the
    // literal last message: a fast phase (e.g. the Product Profile / EEI check,
    // or any phase in step mode) can emit phase_completed immediately followed by
    // phase_paused/awaiting_decision, committing BOTH the PHASE_RESULT card and
    // its WORKFLOW_CONTROL ("Click Continue") follow-up in the SAME render batch.
    // Attaching to `messages[messages.length - 1]` then lands the block on the
    // control prompt, so the result card renders ABOVE its own thinking block.
    // (Slow, single-batch phases like NRC/ITAR happened to dodge this because
    // their pause arrives in a later batch.) Scan backward instead, past anything
    // that can never carry the block (progress lines, the control prompt), to the
    // real PHASE_RESULT/answer target. Applies to every phase — schedule_b, which
    // can also re-narrate a whole phase sequence, relies on the same scan.
    // Prefer the PHASE_RESULT card that matches THIS block's phase. A terminal batch can
    // commit several phase cards at once (a nuclear/ITAR hit pushes NRC + ITAR + EAR/CCL
    // together); the generic backward-scan below would then attach the block to the LAST
    // card (so NRC's thinking rendered above the EAR card). The phaseKey — stamped on the
    // 'phase'-level line by phase_started — lets us attach it to the RIGHT card, so each
    // phase's progress folds into its own card and the feed reads progress → card in order.
    const bufferPhaseKey = thoughtStepsRef.current.find((s) => s.phaseKey)
      ?.phaseKey as ExportPhaseKey | undefined;
    const bufferStage = bufferPhaseKey
      ? EXPORT_PHASE_STAGE[bufferPhaseKey]
      : undefined;

    let targetId: number | null = null;
    let sawControlWithoutTarget = false;

    if (bufferStage) {
      // Most-recent PHASE_RESULT for this phase that has no block yet.
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const candidate = messages[i];
        if (
          candidate?.type === 'PHASE_RESULT' &&
          candidate.stage === bufferStage &&
          !candidate.thoughtSteps
        ) {
          targetId = candidate.id;
          break;
        }
      }
    }

    if (targetId === null) {
      // Generic backward-scan (original behaviour): used when the block has no phaseKey
      // or its phase card isn't in the feed yet. Skips progress lines and the
      // WORKFLOW_CONTROL "Click Continue" prompt to reach the real PHASE_RESULT/answer
      // target — the fast-phase case where phase_completed + phase_paused commit in one
      // batch and attaching to messages[last] would land the block on the control prompt.
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const candidate = messages[i];
        if (!candidate || candidate.sender !== 'bot') {
          break;
        }
        if (
          candidate.text === 'Thinking...' ||
          candidate.type === 'STEP_PROGRESS' ||
          candidate.type === 'PHASE_PROGRESS'
        ) {
          continue;
        }
        if (candidate.thoughtSteps) {
          // Already has its own block from an earlier flush — nothing further
          // back in this run of bot messages is still waiting for one.
          break;
        }
        if (candidate.type === 'WORKFLOW_CONTROL') {
          // Never a valid target: its narration belongs on the PHASE_RESULT
          // card, not the "click Continue" prompt. Keep scanning backward in
          // case that card landed in the same batch, right before it.
          sawControlWithoutTarget = true;
          continue;
        }
        targetId = candidate.id;
        break;
      }
    }

    if (targetId === null && !sawControlWithoutTarget) {
      // Nothing eligible showed up yet — wait for more messages instead of
      // dropping this turn's narration.
      return;
    }

    const capturedSteps = thoughtStepsRef.current;
    const capturedSeconds = thinkingStartedAtRef.current
      ? Math.max(
          1,
          Math.round((Date.now() - thinkingStartedAtRef.current) / 1000),
        )
      : thinkingElapsedSeconds;
    thoughtStepsRef.current = [];
    seenStepKeysRef.current = new Set();
    thinkingStartedAtRef.current = null;

    if (targetId === null) {
      // Only a WORKFLOW_CONTROL landed this turn, with no PHASE_RESULT/answer
      // alongside it to carry the narration — discard rather than attach it
      // to a message that should never show one.
      return;
    }

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === targetId
          ? {
              ...msg,
              thoughtSteps: capturedSteps,
              thoughtSeconds: capturedSeconds,
            }
          : msg,
      ),
    );
  }, [messages, thinkingElapsedSeconds]);

  const submitClassification = async (
    product_desc: string,
    followups: iFollowupItem[],
    command?: WorkflowCommand,
  ) => {
    const workflowStateForSubmit = Object.keys(
      workflowStateRef.current || {},
    ).length
      ? workflowStateRef.current
      : workflowState;
    const clf = {
      clf_id: clfId,
      product_desc: product_desc,
      group_id: cur_user?.selected_team?.id,
      followup_items: followups,
      workflow_state: workflowStateForSubmit,
      ...(command ? { command: command } : {}),
    };
    connectWebSocket('/ws/export_workflow', clf, { sendAccessToken: true });
  };

  const handleSubmit = async (inputValue: string) => {
    // const hasCreditsToStart = await ensureCredits(STEP_CREDIT_COST, {
    //   refresh: true,
    // });
    // if (!hasCreditsToStart) {
    //   return;
    // }

    if (!HTSCode && inputValue !== '') {
      logAppAction('export_workflow:started');
      setUserProductDesc(inputValue);
      setCurrentStage(1);
      try {
        await submitClassification(inputValue, followupItems);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleFollowupSubmit = (responses: iFollowupItem[]) => {
    setFollowupItems(responses);
    submitClassification(userProductDesc, responses);
  };

  const getWorkflowActionLabel = (
    action: WorkflowCommandAction,
    targetPhase?: ExportPhaseKey,
  ) => {
    if (action === 'goto') {
      return targetPhase ? EXPORT_PHASE_LABEL[targetPhase] : 'Go to phase';
    }

    if (action === 'run_all') {
      return 'Complete Workflow';
    }

    return 'Continue Workflow';
  };

  const handleWorkflowCommand = async (
    action: WorkflowCommandAction,
    targetPhase?: ExportPhaseKey,
  ) => {
    if (isStreaming) {
      return;
    }

    // const hasCreditsForNextStage = await ensureCredits(STEP_CREDIT_COST, {
    //   refresh: true,
    // });
    // if (!hasCreditsForNextStage) {
    //   return;
    // }

    const optimisticPhase =
      targetPhase ||
      (action === 'next' || action === 'run_all'
        ? EXPORT_PHASE_ORDER.find(
            (phase) =>
              EXPORT_PHASE_ORDER.indexOf(phase) >
                EXPORT_PHASE_ORDER.indexOf(activePhase || 'schedule_b') &&
              phaseStatusRef.current[phase] !== 'complete',
          )
        : undefined);
    if (optimisticPhase) {
      setUiActivePhase(optimisticPhase);
    }

    const label = getWorkflowActionLabel(action, targetPhase);
    thoughtStepsRef.current = [];
    seenStepKeysRef.current = new Set();
    thinkingStartedAtRef.current = Date.now();
    setThinkingElapsedSeconds(0);
    setMessages((prev) => [
      ...prev.filter((msg) => msg.text !== 'Thinking...'),
      {
        id: nextMessageId(),
        sender: 'user',
        text: label,
      },
      { id: nextMessageId(), sender: 'bot', text: 'Thinking...' },
    ]);

    const productDesc = userProductDesc || cmdtDesc || input || '';
    const command: WorkflowCommand =
      action === 'goto' && targetPhase
        ? { action: 'goto', target_phase: targetPhase }
        : { action: action as Exclude<WorkflowCommandAction, 'goto'> };
    await submitClassification(productDesc, followupItems, command);
    setInput('');
  };

  // Returns the phase currently underway, or null when nothing is running
  // (e.g. at a between-phases decision pause, where jumping is allowed).
  const getRunningPhase = (): ExportPhaseKey | null => {
    const inProgress = EXPORT_PHASE_ORDER.find(
      (p) => phaseStatusRef.current[p] === 'in_progress',
    );
    if (inProgress) return inProgress;
    // Streaming has begun but no phase has reported in_progress yet.
    if (isStreaming && activePhase) return activePhase as ExportPhaseKey;
    return null;
  };
  const isWorkflowWorkInProgress = Boolean(getRunningPhase());
  const hasPendingFollowupQuestion = followupItems.some(
    (item) => !hasFollowupAnswer(item),
  );
  const isMessageInputDisabled =
    !userHasMonitor ||
    insufficientCreditsOpen ||
    (isWorkflowWorkInProgress && !hasPendingFollowupQuestion);

  useEffect(() => {
    if (isMessageInputDisabled || isModalOpen) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [isMessageInputDisabled, isModalOpen]);

  const handleGotoPhase = (targetPhase: ExportPhaseKey) => {
    // View-only (scroll to the phase's "Thought" heading, no goto) when the phase is
    // either COMPLETE (has a result to view) or genuinely NOT APPLICABLE to this run —
    // i.e. the EEI step marked it invalid (Schedule B on an intangible export). Those must
    // NOT dispatch a goto: re-running a done phase or a phase that has nothing to do is the
    // "clicking a skipped panel gets stuck starting it" bug.
    //
    // A phase that is merely 'skipped' because the user JUMPED PAST it is still runnable —
    // fall through so the click dispatches the goto and starts it. not_applicable is a
    // per-phase flag on the artifact; only Schedule B carries it now (the Country and Control
    // List Check is never wholly N/A — only its control-list part can be), so every other
    // 'skipped' phase is a jump-over.
    const targetStatus = phaseStatusRef.current[targetPhase];
    const targetNotApplicable = Boolean(
      (workflowDataRef.current as any)?.[EXPORT_PHASE_DATA_KEY[targetPhase]]
        ?.not_applicable,
    );
    if (
      targetStatus === 'complete' ||
      (targetStatus === 'skipped' && targetNotApplicable)
    ) {
      scrollToPhaseHeading(targetPhase);
      return;
    }

    // Users may jump straight to any phase — the workflow no longer forces the
    // Product Profile & EEI Check to run first. Each phase gathers the inputs it
    // needs on the server when it's entered without the profile facts.

    // Block manual switching while a *different* phase is actively running.
    // Backend-driven transitions don't flow through here, so they're unaffected.
    const runningPhase = getRunningPhase();
    if (runningPhase && runningPhase !== targetPhase) {
      setRunningPhaseLabel(EXPORT_PHASE_LABEL[runningPhase]);
      setAttemptedPhaseLabel(EXPORT_PHASE_LABEL[targetPhase]);
      setIsPhaseRunningModalOpen(true);
      return;
    }

    // Clicking the panel of the phase already underway is a no-op: there is nothing
    // to jump to (you're already there) and dispatching a goto would restart the
    // phase on the server, wiping the in-progress intake. Leave it waiting on its
    // current question instead.
    if (runningPhase && runningPhase === targetPhase) {
      return;
    }

    handleWorkflowCommand('goto', targetPhase);
  };

  const handleCloseModal = () => {
    cancelPendingLeaveSave();
    setModalOpen(false);
  };

  const handleEndWorkflowSave = () => {
    setIsEndWorkflowModalOpen(false);
    handleOptionClick('Save Workflow');
  };

  // Mirrors handleLeaveWithoutSaving — there's no pending navigation to
  // resume here (this is triggered from the in-chat "End Workflow" action,
  // not a blocked route change), so just discard the session in place.
  const handleEndWorkflowWithoutSaving = () => {
    setIsEndWorkflowModalOpen(false);
    startOver();
  };

  const handleTypedWorkflowOption = (
    value: string,
  ): 'handled' | 'invalid' | 'none' => {
    const latestBotMessage = [...messages]
      .reverse()
      .find(
        (message) =>
          message.sender === 'bot' && message.text !== 'Thinking...',
      );

    if (
      latestBotMessage?.type !== 'WORKFLOW_CONTROL' &&
      latestBotMessage?.type !== 'FINAL_RESULT'
    ) {
      return 'none';
    }

    const currentWorkflowSignature = getCurrentWorkflowSaveSignature();
    const savedWorkflowIsCurrent =
      Boolean(lastSavedWorkflowSignature) &&
      lastSavedWorkflowSignature === currentWorkflowSignature;
    const canViewSavedWorkflow = Boolean(
      ticketId && !isResumedTicket && savedWorkflowIsCurrent,
    );
    const saveLabel = canViewSavedWorkflow ? 'View Saved' : 'Save Workflow';
    const options: Array<{
      label: string;
      aliases: string[];
      onSelect: () => void;
    }> = [];

    if (latestBotMessage.type === 'WORKFLOW_CONTROL') {
      const control = latestBotMessage.payload?.control;
      const availableActions: WorkflowCommandAction[] =
        control?.available_actions || [];
      const controlPhase =
        normalizeActivePhase(control?.active_phase || '') ||
        getPhaseKeyForStage(latestBotMessage.stage);
      const alwaysShowIntermediateActions =
        controlPhase === 'nrc' || controlPhase === 'itar';
      const isTerminalCcl = controlPhase === 'ccl';
      const canGoNext =
        !isTerminalCcl &&
        (alwaysShowIntermediateActions || availableActions.includes('next'));
      const canEnd =
        alwaysShowIntermediateActions ||
        isTerminalCcl ||
        availableActions.includes('run_all');

      if (canGoNext) {
        options.push({
          label: 'Continue Workflow',
          aliases: ['continue', 'continue workflow'],
          onSelect: () =>
            handleWorkflowCommand(
              'next',
              normalizeActivePhase(control?.next_phase || '') || undefined,
            ),
        });
      }

      if (canEnd) {
        options.push({
          label: 'End Workflow',
          aliases: ['end', 'end workflow'],
          onSelect: () => setIsEndWorkflowModalOpen(true),
        });
      }
    }

    options.push({
      label: saveLabel,
      aliases: ['save', 'save work', 'save workflow', 'view saved'],
      onSelect: () => {
        if (canViewSavedWorkflow) {
          history.push(`/monitor/ticket/${ticketId}`);
          return;
        }
        handleOptionClick('Save Workflow');
      },
    });

    const isTerminalFinal = latestBotMessage.type === 'FINAL_RESULT';
    const workflowControlPhase = normalizeActivePhase(
      latestBotMessage.payload?.control?.active_phase || '',
    );
    if (isTerminalFinal || workflowControlPhase === 'ccl') {
      options.push({
        label: 'New Session',
        aliases: ['new', 'new session'],
        onSelect: () => handleOptionClick('New Session'),
      });
    }

    const normalizedValue = value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[.!?]+$/, '');
    const numericChoice = normalizedValue.match(/^(\d+)[.)]?$/);
    const numberWordChoice = [
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
    ].indexOf(normalizedValue);
    const selectedOption = numericChoice
      ? options[Number(numericChoice[1]) - 1]
      : numberWordChoice >= 0
      ? options[numberWordChoice]
      : options.find(
          (option) =>
            option.label.toLowerCase() === normalizedValue ||
            option.aliases.includes(normalizedValue),
        );

    if (!selectedOption) {
      enqueueSnackbar(
        `Choose ${options
          .map((option, index) => `${index + 1} (${option.label})`)
          .join(' or ')}.`,
        { variant: 'warning' },
      );
      return 'invalid';
    }

    selectedOption.onSelect();
    return 'handled';
  };

  const handleSend = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (isMessageInputDisabled) {
      return;
    }

    const value = input.trim();
    if (!value) {
      return;
    }

    const workflowOptionResult = handleTypedWorkflowOption(value);
    if (workflowOptionResult !== 'none') {
      if (workflowOptionResult === 'handled') {
        setInput('');
      }
      return;
    }

    // if (!creditsLoaded) {
    //   enqueueSnackbar('Loading team credits. Please try again in a moment.', {
    //     variant: 'info',
    //   });
    //   return;
    // }

    dispatchUserMessage(value);
  };

  // Landing page Start: the scope choice rides in workflow_state._control (pyapi
  // `_accept_scope_choice` normalises it; no new WS field), then the description goes out as
  // the ordinary opening message. Merged onto whatever state is already seeded (e.g. the Design
  // Advisor's facts, which live in `workflowState` until the first frame fills the ref).
  const handleLandingStart = (choice: ExportScopeChoice) => {
    if (isMessageInputDisabled) {
      return;
    }
    const value = input.trim();
    if (!value) {
      return;
    }
    const base = Object.keys(workflowStateRef.current || {}).length
      ? workflowStateRef.current
      : workflowState || {};
    const nextWorkflowState = {
      ...base,
      _control: { ...(base?._control || {}), ...scopeControlFields(choice) },
    };
    workflowStateRef.current = nextWorkflowState;
    setWorkflowState(nextWorkflowState);
    dispatchUserMessage(value);
  };

  // Posts a user turn (bubble + "Thinking..." placeholder) and sends it — as the answer to
  // pending follow-ups when there are any, else as the opening/next message. An option-card
  // click passes `option`: the bubble shows its label, the clicked question's item carries its
  // id as `selected_option_id`, and the composer text is left alone.
  const dispatchUserMessage = (
    value: string,
    option?: { id: string; label: string; question: string },
  ) => {
    if (userProductDesc === '') {
      setUserProductDesc(value);
    }

    const newMessage: IMessage = {
      id: nextMessageId(),
      sender: 'user',
      text: option?.label || value,
    };
    thoughtStepsRef.current = [];
    seenStepKeysRef.current = new Set();
    thinkingStartedAtRef.current = Date.now();
    setThinkingElapsedSeconds(0);
    setMessages((prev) => [...prev, newMessage]);
    if (!option) {
      setInput('');
    }
    window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);
    // Simulate bot response — only while the turn is still streaming: a fast deterministic
    // reply (a code card, a pause) can finish inside 800 ms, and a placeholder added after
    // that stayed on screen as an empty "Thinking…" while Idle (PR review 2026-10-08).
    setTimeout(() => {
      if (!isStreamingLiveRef.current) {
        return;
      }
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), sender: 'bot', text: 'Thinking...' },
      ]);
    }, 800);

    if (currentStage === 1) {
      if (followupItems.length > 0) {
        // An option click answers ONLY the question it belongs to — other pending items (e.g. a
        // question left open before a jump) keep their empty answer. A typed reply keeps the
        // legacy behaviour: it fills every still-unanswered item.
        const isClickedItem = (item: iFollowupItem) =>
          Boolean(option) && item.question === option?.question && !hasFollowupAnswer(item);
        const clickMatchesPendingItem = followupItems.some(isClickedItem);
        const itemsWithDefaultAnswers = followupItems.map((item) => {
          if (option && clickMatchesPendingItem) {
            return isClickedItem(item)
              ? { ...item, selected_option_id: option.id, answer: value }
              : item;
          }
          return { ...item, answer: item.answer ?? value };
        });

        handleFollowupSubmit(itemsWithDefaultAnswers);
      } else {
        handleSubmit(value);
      }
    }
  };

  // Slice B: send the user to the Classify tool in this tab. The chat is snapshotted into the
  // sessionStorage handoff record first (restored on return — see the mount effect), the leave
  // guard is bypassed (the user's intent is explicit) and the session is NOT reset.
  const startClassifyHandoff = (item: iFollowupItem) => {
    if (isStreaming) {
      return;
    }
    // Never hand Classify a commodity code typed into the description: the workflow may have
    // rejected it, and Classify would anchor on its chapter.
    const productDescription = stripCommodityCodes(
      workflowData?.product?.description || userProductDesc || '',
    );
    const saved = saveExportClassifyHandoff<ExportChatRecoverableState>({
      clfId: clfId,
      snapshot: exportRecoverableStateRef.current,
      productDescription: productDescription,
      pendingQuestion: item.question,
    });
    if (!saved) {
      enqueueSnackbar('Could not open the Classify tool. Please try again.', {
        variant: 'error',
      });
      return;
    }
    try {
      window.sessionStorage.setItem('autoTab', 'HTS Classification');
    } catch (error) {
      // ignore — the Classify page defaults to the HTS tab
    }
    allowNavigationRef.current = true;
    history.push('/bite-classify', { exportHandoff: { productDescription: productDescription } });
    window.setTimeout(() => {
      allowNavigationRef.current = false;
    }, 0);
  };

  // A click on a follow-up option card. `answer` options send their value (+ id);
  // `focus_composer` moves focus; `open_classify_tool` starts the Classify round trip.
  const handleFollowupOptionSelect = (
    msg: IMessage,
    item: iFollowupItem,
    option: iFollowupOption,
  ) => {
    if (option.action === 'open_classify_tool') {
      startClassifyHandoff(item);
      return;
    }
    if (option.action && option.action !== 'answer') {
      composerInputRef.current?.focus();
      return;
    }
    if (isStreaming || !option.value) {
      return;
    }
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? { ...m, payload: { ...(m.payload || {}), selectedOptionId: option.id } }
          : m,
      ),
    );
    dispatchUserMessage(option.value, {
      id: option.id,
      label: option.label,
      question: item.question,
    });
  };

  // A follow-up card is live while its question is still unanswered AND it is the latest card
  // for that question (a re-asked question supersedes the older card).
  const isFollowupCardActive = (msg: IMessage, msgIndex: number) => {
    const question = msg.payload?.item?.question;
    if (!question) return false;
    const pending = followupItems.some(
      (item) => item.question === question && !hasFollowupAnswer(item),
    );
    if (!pending) return false;
    return !messages
      .slice(msgIndex + 1)
      .some((m) => m.type === 'FOLLOWUP_CARD' && m.payload?.item?.question === question);
  };

  // Only chat commands come through here ('Save Workflow' / 'Save Work' / 'New Session') — never
  // let one stand in for the product description (Bug 9: "Save Work" became the saved opening
  // message and the product_desc sent on later turns).
  const handleOptionClick = (option: string) => {
    if (
      (option === 'Save Workflow' || option === 'Save Work') &&
      currentStage === 1
    ) {
      logAppAction('export_workflow:save_results');
      if (ticketDraft?._id || ticketId) {
        saveTicket(ticketDraft?.ticketName || newTicketName || '');
        return;
      }

      setModalOpen(true);
      return;
    } else if (option === 'New Session') {
      startOver();
      return;
    }
    const newMessage: IMessage = {
      id: nextMessageId(),
      sender: 'user',
      text: option,
    };
    thoughtStepsRef.current = [];
    seenStepKeysRef.current = new Set();
    thinkingStartedAtRef.current = Date.now();
    setThinkingElapsedSeconds(0);
    setMessages((prev) => [...prev, newMessage]);
    // Simulate bot response — only while the turn is still streaming: a fast deterministic
    // reply (a code card, a pause) can finish inside 800 ms, and a placeholder added after
    // that stayed on screen as an empty "Thinking…" while Idle (PR review 2026-10-08).
    setTimeout(() => {
      if (!isStreamingLiveRef.current) {
        return;
      }
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), sender: 'bot', text: 'Thinking...' },
      ]);
    }, 800);

    if (followupItems.length > 0) {
      const itemsWithDefaultAnswers = followupItems.map((item) => ({
        ...item,
        answer: item.answer ?? option, // or any default value you want
      }));

      handleFollowupSubmit(itemsWithDefaultAnswers);
    } else {
      handleSubmit(option);
    }

    setInput('');
  };

  // Scrolls the chat's own scroll container (not the page) to a settled
  // phase's "Thought" heading, without relying on scrollIntoView's
  // ancestor-walking (see useScrollWorkflowIntoView above for the same
  // rationale).
  const scrollToPhaseHeading = (phase: ExportPhaseKey) => {
    const headingEl = phaseHeadingRefs.current[phase];
    const container = chatScrollRef.current;
    if (!headingEl || !container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const headingRect = headingEl.getBoundingClientRect();
    const targetTop =
      container.scrollTop + (headingRect.top - containerRect.top) - 16;

    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  };

  // The most recent phase-level entry in a thought-steps array acts as that
  // block's title, hoisted above the "Thought for Ns" summary (see the
  // finished/live thought renders below) — a short uppercase heading (e.g.
  // "PRODUCT PROFILE") matching the right-rail Classification Progress labels.
  const getTitleThoughtStepIndex = (steps: EarThoughtStep[]) =>
    steps.reduce(
      (found, step, index) =>
        step.level === 'phase' && step.phaseKey ? index : found,
      -1,
    );

  // Renders the phase → group → step hierarchy shared by the finished
  // "Thought for Ns" block and the live "Thinking..." block: each phase's own
  // short heading (skipped for `titleStepIndex`, since that one is already
  // hoisted above as the block's title) followed by its overview sentence,
  // indented group sub-headings, and indented step bullets — appended in
  // order as they arrive, never replacing an earlier line.
  const renderThoughtStepEntries = (
    steps: EarThoughtStep[],
    keyPrefix: string,
    titleStepIndex: number,
    isLive = false,
  ) => {
    // While still streaming, each newly-appended line gets a brief fade-in
    // (mirrors how the finished list is fully static once settled).
    const liveLineClass = isLive ? ' standard-workflow-thought-line-in' : '';

    return steps.map((step, stepIndex) => {
      if (step.level === 'phase') {
        const heading =
          stepIndex !== titleStepIndex && step.phaseKey
            ? EXPORT_PHASE_CHAT_HEADING[step.phaseKey]
            : undefined;
        return (
          <React.Fragment key={`${keyPrefix}-${stepIndex}`}>
            {heading && (
              <div
                ref={(el) => {
                  phaseHeadingRefs.current[step.phaseKey as ExportPhaseKey] =
                    el;
                }}
                className={`standard-workflow-thought-phase-heading${liveLineClass}`}
              >
                {heading}
              </div>
            )}
            <div
              className={`standard-workflow-thought-goal${liveLineClass}`}
            >
              {step.text}
            </div>
          </React.Fragment>
        );
      }
      if (step.level === 'group') {
        return (
          <div
            className={`standard-workflow-thought-group${liveLineClass}`}
            key={`${keyPrefix}-${stepIndex}`}
          >
            {step.text}
          </div>
        );
      }
      if (step.level === 'finding') {
        // A DETERMINATION — the result of a step. Reuses the SAME row markup + class as a
        // progress step (`standard-workflow-thought-step`) so the dot is IDENTICAL and
        // stays in sync if the step dot ever changes; only the text gets a touch more
        // contrast (`standard-workflow-thought-finding`) so it still reads as a conclusion.
        return (
          <div
            className={`standard-workflow-thought-step standard-workflow-thought-finding${liveLineClass}`}
            key={`${keyPrefix}-${stepIndex}`}
          >
            <span aria-hidden='true' />
            <div>{step.text}</div>
          </div>
        );
      }
      return (
        <div
          className={`standard-workflow-thought-step${liveLineClass}`}
          key={`${keyPrefix}-${stepIndex}`}
        >
          <span aria-hidden='true' />
          <div>{step.text}</div>
        </div>
      );
    });
  };

  const handleTicket = async (values: any, savedSignature?: string) => {
    const new_case = {
      ...ticketDraft,
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const existingCaseId = new_case?._id || ticketId;
    const resp = existingCaseId
      ? await CasesAPI.updateCase({
          ...new_case,
          _id: existingCaseId,
        })
      : await CasesAPI.createCase({ newCase: new_case });
    setTicketDraft(new_case);

    if (resp?.success) {
      enqueueSnackbar(
        existingCaseId
          ? 'Successfully updated saved workflow'
          : 'Successfully created new ticket',
        {
          variant: 'success',
        },
      );
      setTicketID(resp.result._id || existingCaseId);
      setLastSavedWorkflowSignature(
        savedSignature || getCurrentWorkflowSaveSignature(),
      );
      setIsResumedTicket(false);
      if (ticketType === 'Done') {
        resetTicketDraft();
      } else {
        setTicketDraft({
          ...new_case,
          _id: resp.result._id || existingCaseId,
        });
      }
      shareEmail.forEach((email) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          values.ticketName,
          cur_user,
          email,
          resp.result._id || existingCaseId,
        );
      });
      setSavingTicket(false);
      setModalOpen(false);
      setNewTicketName('');
      completePendingLeaveAfterSave();
    } else if (resp) {
      console.error('[EARChatUI] Failed to save export workflow ticket:', resp);
      const error_msg =
        'Error saving new ticket, please try again and contact the BITE Team if the problem persist';
      enqueueSnackbar(error_msg, { variant: 'error' });
      leaveSaveInProgressRef.current = false;
      setSavingTicket(false);
      setModalOpen(false);
    }
  };

  const saveTicket = async (name: string) => {
    setSavingTicket(true);
    const {
      workflowData: workflowDataForSave,
      workflowState: workflowStateForSave,
      phaseStatus,
    } = getCurrentWorkflowSaveSnapshot();
    const isCurrentWorkflowComplete = calculateIsComplete(phaseStatus);
    const savedSignature = getCurrentWorkflowSaveSignature();
    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };
    // The verbatim opening message (backend echo, else the description the chat started with).
    // Never a chat command label (Bug 9).
    const openingMessage = [initialRequestRef.current, userProductDesc]
      .map((value) => String(value || '').trim())
      .find((value) => value && !isExportCommandLabel(value)) || '';
    // Bug 9: the opening message + the chat as shown ride INSIDE the workflow snapshot — the Case
    // schema has no field for them, so top-level extras (e.g. `initialRequest`) are dropped.
    const workflowDataSnapshot = toJsonSafe({
      ...workflowDataForSave,
      _workflowStateJson: JSON.stringify(workflowStateForSave),
      [EXPORT_INITIAL_REQUEST_KEY]: openingMessage,
      [EXPORT_CHAT_TRANSCRIPT_KEY]: serializeExportChatTranscript(messages),
    });
    const followUpsForSave = toJsonSafe(followupItems || []);
    const streamDataForSave = toJsonSafe(streamData || []);

    const query: any = {
      ...(ticketDraft?._id || ticketId ? { _id: ticketDraft?._id || ticketId } : {}),
      owner: ownerQuery,
      ticketName: name || ticketDraft?.ticketName || 'Export Workflow',
      lastUpdate: formatted_dt,
      activityType: 'workflow',
      workflowType: 'export',
      docType: 'Export Unified',
      sharedWith: shareEmail,
      frequency: frequency,
      activeFilter: '',
      selectedOpts: [],
      status: isCurrentWorkflowComplete ? 'Complete' : 'In Progress',
      favorite: false,
      groupName: cur_user.selected_team.name,
      classificationValue:
        openingMessage ||
        workflowDataForSave?.product?.description ||
        cmdtDesc ||
        '',
      // Verbatim opening message (backend echo). NB: Mongoose drops this top-level field (not in
      // the Case schema) — the copy that persists is the snapshot's `_initialRequest` above.
      initialRequest: openingMessage,
      followUps: followUpsForSave,
      aiStream: streamDataForSave,
      workflowData: [workflowDataSnapshot],
      exNotes: explanatoryNotes || '',
      chapterNotes: sectionNotes || '',
      crossRulings: crossLinks || [],
      commodity: {
        commodityName:
          cmdtDesc || workflowDataForSave?.product?.description || '',
        harmonizedScheduleCode: HTSCode || '',
        eccn: HTSCode || '',
        destinationCountry:
          workflowDataForSave?.country_screening?.input_destination_country ||
          workflowDataForSave?.country_screening?.normalized_country_name ||
          '',
      },
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: ticketDraft?._id || ticketId
          ? 'updated saved export workflow'
          : 'created classification ticket',
        timeStamp: Date.now(),
      },
    };
    handleTicket(query, savedSignature);
  };

  const handleSaveTicketFromLeaveModal = () => {
    if (!userHasMonitor) {
      return;
    }

    leaveSaveInProgressRef.current = true;
    setIsLeaveModalOpen(false);
    if (ticketDraft?._id || ticketId) {
      saveTicket(ticketDraft?.ticketName || newTicketName || '');
      return;
    }

    setModalOpen(true);
  };

  const renderSaveButton = (stage: number, onClickHandler: () => void) => {
    const currentWorkflowSignature = getCurrentWorkflowSaveSignature();
    const savedWorkflowIsCurrent =
      Boolean(lastSavedWorkflowSignature) &&
      lastSavedWorkflowSignature === currentWorkflowSignature;

    if (ticketId && !isResumedTicket && savedWorkflowIsCurrent) {
      return (
        <Link
          to={`/monitor/ticket/${ticketId}`}
          className='option-button'
        >
          View Saved
        </Link>
      );
    }
    return (
      <Tooltip
        title={'Save the current workflow work to Saved Work.'}
        placement='bottom-start'
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              display: 'flex',
              padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              borderRadius: 'var(--radius-md, 8px)',
              background: 'var(--Background-200, var(--bg-colors))',
              color: 'var(--text-prim25',
              maxWidth: '700px',
              '& .MuiTooltip-arrow': {
                color: 'var(--Background-200, var(--bg-colors))',
              },
            },
          },
        }}
      >
        <button
          type='button'
          className='option-button'
          onClick={onClickHandler}
        >
          {stage === 3 ? 'Save Workflow' : 'Save'}
        </button>
      </Tooltip>
    );
  };

  const renderWorkflowControlOptions = (
    msg: IMessage,
    isLastMessage: boolean,
  ) => {
    const control = msg.payload?.control;
    const availableActions: WorkflowCommandAction[] =
      control?.available_actions || [];
    const controlPhase =
      normalizeActivePhase(control?.active_phase || '') ||
      getPhaseKeyForStage(msg.stage);
    const alwaysShowIntermediateActions =
      controlPhase === 'nrc' || controlPhase === 'itar';
    const isTerminalCcl = controlPhase === 'ccl';
    const canGoNext =
      !isTerminalCcl &&
      (alwaysShowIntermediateActions || availableActions.includes('next'));
    const canEnd =
      alwaysShowIntermediateActions ||
      isTerminalCcl ||
      availableActions.includes('run_all');

    return (
      <>
        <div className='message-bubble'>
          <WorkflowStreamingResponse
            text={msg.text}
            animate={isLastMessage}
          />
          <ol className='options-container workflow-control-options'>
            {canGoNext && (
              <li>
                <button
                  type='button'
                  className='option-button'
                  onClick={() =>
                    handleWorkflowCommand(
                      'next',
                      normalizeActivePhase(control?.next_phase || '') ||
                        undefined,
                    )
                  }
                  disabled={isStreaming || insufficientCreditsOpen}
                >
                  Continue Workflow
                </button>
              </li>
            )}
            {canEnd && (
              <li>
                <button
                  type='button'
                  className='option-button'
                  onClick={() => setIsEndWorkflowModalOpen(true)}
                  disabled={isStreaming || insufficientCreditsOpen}
                >
                  End Workflow
                </button>
              </li>
            )}
            <li>
              {renderSaveButton(3, () => handleOptionClick('Save Workflow'))}
            </li>
            {isTerminalCcl && (
              <li>
                <button
                  type='button'
                  className='option-button'
                  onClick={() => handleOptionClick('New Session')}
                >
                  New Session
                </button>
              </li>
            )}
          </ol>
        </div>
      </>
    );
  };

  const renderPhaseResultCard = (msg: IMessage) => {
    const phaseWorkflowData = mergeWorkflowData(
      msg.payload?.workflowData || {},
      workflowData,
    );
    const phaseWorkflowState = msg.payload?.workflowState || {};
    const phaseScheduleBCode =
      phaseWorkflowData?.export_screening?.hs_code ||
      phaseWorkflowData?.schedule_b?.hs_code ||
      phaseWorkflowData?.schedule_b?.schedule_b_code ||
      HTSCode ||
      '';
    const phaseCountry =
      phaseWorkflowData?.export_screening?.destination_country ||
      phaseWorkflowData?.country_screening?.normalized_country_name ||
      phaseWorkflowData?.country_screening?.input_destination_country ||
      originCntry ||
      '';

    switch (msg.stage as EarStage) {
      case 'schedule_b':
        return (
          <ScheduleBResults
            saveBtn={undefined}
            handleRowClick={() => {
              return;
            }}
            workflowData={phaseWorkflowData}
            isSelected={false}
            isExpands={true}
          />
        );
      case 'country_screening':
        return (
          <CountryControlListResults
            primary={phaseScheduleBCode}
            country={phaseCountry}
            workflowData={phaseWorkflowData}
            isExpands={true}
          />
        );
      case 'profile':
        return (
          <ProductProfileResults
            saveBtn={undefined}
            handleRowClick={() => {
              return;
            }}
            workflowData={phaseWorkflowData}
            isSelected={false}
            isExpands={true}
          />
        );
      case 'export_screening':
        // A pre-merge chat's separate control-list (old "Sensitive Tech") result message.
        return (
          <CountryControlListResults
            primary={phaseScheduleBCode}
            country={phaseCountry}
            workflowData={phaseWorkflowData}
            isExpands={true}
            showCountryPart={false}
          />
        );
      case 'nrc':
        return (
          <NRCResults
            saveBtn={undefined}
            handleRowClick={() => {
              return;
            }}
            workflowData={phaseWorkflowData}
            isSelected={false}
            isExpands={true}
          />
        );
      case 'itar':
        return (
          <ITARResults
            saveBtn={undefined}
            handleRowClick={() => {
              return;
            }}
            workflowData={phaseWorkflowData}
            isSelected={false}
            isExpands={true}
          />
        );
      case 'ccl':
        return (
          <CCLResults
            saveBtn={undefined}
            handleRowClick={() => {
              return;
            }}
            workflowData={phaseWorkflowData}
            workflowState={phaseWorkflowState}
            currentStep={phaseWorkflowState?._meta?.current_step}
            stepsStatus={phaseWorkflowState?._meta?.steps_status}
            isSelected={false}
            isExpands={true}
          />
        );
      default:
        return null;
    }
  };

  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  const exportScreeningData = workflowData?.export_screening || {};
  const rightRailCountry =
    exportScreeningData?.destination_country ||
    workflowData?.country_screening?.normalized_country_name ||
    workflowData?.country_screening?.input_destination_country ||
    originCntry ||
    '';
  const headerSavedWorkflowId = ticketId || ticketDraft?._id;
  const headerWorkflowIsSaved =
    Boolean(headerSavedWorkflowId) &&
    Boolean(lastSavedWorkflowSignature) &&
    lastSavedWorkflowSignature === getCurrentWorkflowSaveSignature();
  const getTimelineStatus = (
    phase: ExportPhaseKey,
    status: string,
  ): WorkflowStepStatus => {
    // An operationally settled phase whose automated assessment is unusable renders as
    // amber `needs_review`, never the completed marker (resilience plan Slice 6). This is
    // presentation-only — the backend phase_status stays `complete` so orchestration and
    // the "all phases settled" checks elsewhere are untouched.
    if (status === 'complete') {
      // "country" covers both parts of the Country and Control List Check.
      return isPhaseOrPartNotAssessed(workflowData, phase) ? 'needs_review' : 'complete';
    }
    if (status === 'skipped' || status === 'error') {
      return status;
    }

    if (
      status === 'running' ||
      status === 'in_progress' ||
      isPhaseSelected(phase, status)
    ) {
      return 'running';
    }

    return 'pending';
  };
  const getTimelineValue = (value: any) => {
    if (typeof value === 'boolean') {
      return value ? 'Controlled' : 'Not controlled';
    }

    return savedString(value) || undefined;
  };
  const hasSettledTimelineStep = (
    phase: ExportPhaseKey,
    status: string,
  ) => {
    const timelineStatus = getTimelineStatus(phase, status);
    return (
      timelineStatus === 'complete' ||
      timelineStatus === 'needs_review' ||
      timelineStatus === 'skipped' ||
      timelineStatus === 'error'
    );
  };
  const exportProgressStepsBase: WorkflowProgressStep[] = [
    {
      label: 'Product Profile',
      hideHeaderValueWhenExpanded: true,
      hideDetailWhenExpanded: true,
      detail:
        getTimelineValue(workflowData?.profile?.description) ||
        userProductDesc ||
        undefined,
      status: getTimelineStatus('profile', profileStatus),
      notice: <AssessmentStatusBadge data={workflowData} phase='profile' variant='pill' />,
      expandedNotice: <AssessmentStatusBadge data={workflowData} phase='profile' />,
      details: hasSettledTimelineStep('profile', profileStatus)
        ? [
            {
              label: 'Product Information',
              value: getTimelineValue(
                workflowData?.profile?.product_description ||
                  workflowData?.profile?.description,
              ),
            },
            {
              label: 'End Use',
              value: getTimelineValue(workflowData?.profile?.end_use),
            },
            {
              label: 'Export Type',
              value: getTimelineValue(
                workflowData?.profile?.export_type_display,
              ),
            },
            {
              label: 'Destination',
              value: getTimelineValue(
                workflowData?.profile?.destination_country,
              ),
            },
          ]
        : undefined,
      expandedBody: hasSettledTimelineStep('profile', profileStatus) ? (
        <ProductProfileResults
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands
          embedded
        />
      ) : undefined,
      onClick: () => handleGotoPhase('profile'),
    },
    {
      label: 'Schedule B / HTS Classification',
      hideDetailWhenExpanded: true,
      // Not applicable (intangible export) → its reason, not "Awaiting information..." (Bug 6).
      detail: scheduleBTimelineDetail(workflowData?.schedule_b),
      // Settled without a code (not assessed): the badge says so — no "Awaiting information..."
      // body under it (Bug 16).
      hideDetail:
        !scheduleBTimelineDetail(workflowData?.schedule_b) &&
        isPhaseOrPartNotAssessed(workflowData, 'schedule_b'),
      status: getTimelineStatus('schedule_b', scheduleBStatus),
      notice: <AssessmentStatusBadge data={workflowData} phase='schedule_b' variant='pill' />,
      expandedNotice: <AssessmentStatusBadge data={workflowData} phase='schedule_b' />,
      details: hasSettledTimelineStep('schedule_b', scheduleBStatus)
        ? [
            {
              label: 'Schedule B Code',
              value: getTimelineValue(
                workflowData?.schedule_b?.schedule_b_code ||
                  workflowData?.schedule_b?.hs_code,
              ),
            },
            {
              label: 'Product Information',
              value: getTimelineValue(workflowData?.schedule_b?.product_name),
            },
            {
              label: 'Section',
              value: getTimelineValue(workflowData?.schedule_b?.section),
            },
            {
              label: 'Chapter',
              value: getTimelineValue(workflowData?.schedule_b?.chapter),
            },
          ]
        : undefined,
      expandedBody: hasSettledTimelineStep('schedule_b', scheduleBStatus) ? (
        <ScheduleBResults
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands
          embedded
        />
      ) : undefined,
      onClick: () => handleGotoPhase('schedule_b'),
    },
    {
      // Country and Control List Check — the merged phase keeps the old Country card's shape
      // (destination as the detail; no results in the panel).
      label: 'Country and Control List Check',
      hideDetailWhenExpanded: true,
      detail: getTimelineValue(rightRailCountry),
      status: getTimelineStatus('country', countryScreeningStatus),
      notice: <AssessmentStatusBadge data={workflowData} phase='country' variant='pill' />,
      expandedNotice: <AssessmentStatusBadge data={workflowData} phase='country' />,
      details: hasSettledTimelineStep('country', countryScreeningStatus)
        ? [
            {
              label: 'Destination Country',
              value: getTimelineValue(
                workflowData?.country_screening?.normalized_country_name ||
                  workflowData?.country_screening?.input_destination_country,
              ),
            },
            {
              label: 'Embargoed',
              value: getTimelineValue(
                workflowData?.country_screening?.embargoed_countries,
              ),
            },
            {
              label: 'Embargo Details',
              value: getTimelineValue(
                workflowData?.country_screening?.embargo_nature,
              ),
            },
            {
              label: 'OFAC Program',
              value: getTimelineValue(
                workflowData?.country_screening
                  ?.sanctions_programs_and_country_info,
              ),
            },
          ]
        : undefined,
      expandedBody: hasSettledTimelineStep('country', countryScreeningStatus) ? (
        <CountryScreeningResults
          saveBtn={undefined}
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands
          embedded
        />
      ) : undefined,
      onClick: () => handleGotoPhase('country'),
    },
    {
      label: 'Nuclear (NRC + DOE)',
      hideDetail: hasSettledTimelineStep('nrc', nrcStatus),
      detail:
        getTimelineValue(workflowData?.nrc?.reason) ||
        getTimelineValue(workflowData?.nrc?.notes),
      status: getTimelineStatus('nrc', nrcStatus),
      details: hasSettledTimelineStep('nrc', nrcStatus)
        ? [
            {
              label: 'Controlled',
              value: getTimelineValue(workflowData?.nrc?.controlled),
            },
            ...(workflowData?.nrc?.confidence
              ? [
                  {
                    label: 'Confidence',
                    value: getTimelineValue(workflowData?.nrc?.confidence),
                  },
                ]
              : []),
            {
              label: 'Reason',
              value: getTimelineValue(workflowData?.nrc?.reason),
            },
            {
              label: 'Notes',
              value: getTimelineValue(workflowData?.nrc?.notes),
            },
            // Comparative transparency — the competing reading we ruled out (when populated).
            ...(workflowData?.nrc?.closest_alternative ||
            workflowData?.nrc?.closest_alternative_reason
              ? [
                  {
                    label: 'Closest Alternative',
                    value: getTimelineValue(
                      [
                        workflowData?.nrc?.closest_alternative,
                        workflowData?.nrc?.closest_alternative_reason,
                      ]
                        .filter(Boolean)
                        .join(' — ')
                    ),
                  },
                ]
              : []),
            // DOE 10 CFR Part 810 (folded into the nuclear phase) — shown only when present.
            ...(workflowData?.nrc?.doe_controlled
              ? [
                  {
                    label: 'DOE Part 810 Result',
                    value: getTimelineValue(workflowData?.nrc?.doe_controlled),
                  },
                  {
                    label: 'DOE Reason',
                    value: getTimelineValue(workflowData?.nrc?.doe_reason),
                  },
                  ...(workflowData?.nrc?.doe_confidence
                    ? [
                        {
                          label: 'DOE Confidence',
                          value: getTimelineValue(
                            workflowData?.nrc?.doe_confidence
                          ),
                        },
                      ]
                    : []),
                  ...(workflowData?.nrc?.doe_closest_alternative ||
                  workflowData?.nrc?.doe_closest_alternative_reason
                    ? [
                        {
                          label: 'DOE Closest Alternative',
                          value: getTimelineValue(
                            [
                              workflowData?.nrc?.doe_closest_alternative,
                              workflowData?.nrc?.doe_closest_alternative_reason,
                            ]
                              .filter(Boolean)
                              .join(' — ')
                          ),
                        },
                      ]
                    : []),
                ]
              : []),
          ]
        : undefined,
      collapsedBody: hasSettledTimelineStep('nrc', nrcStatus) ? (
        <NRCResults
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands={false}
          embedded
        />
      ) : undefined,
      expandedBody: hasSettledTimelineStep('nrc', nrcStatus) ? (
        <NRCResults
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands
          embedded
        />
      ) : undefined,
      onClick: () => handleGotoPhase('nrc'),
    },
    {
      label: 'ITAR / USML Check',
      hideDetail: hasSettledTimelineStep('itar', itarStatus),
      detail: getTimelineValue(workflowData?.itar?.reason),
      status: getTimelineStatus('itar', itarStatus),
      details: hasSettledTimelineStep('itar', itarStatus)
        ? [
            {
              label: 'Controlled',
              value: getTimelineValue(workflowData?.itar?.controlled),
            },
            {
              label: 'USML Category',
              value: getTimelineValue(workflowData?.itar?.usml_category),
            },
            {
              label: 'Reason',
              value: getTimelineValue(workflowData?.itar?.reason),
            },
            {
              label: 'Confidence',
              value: getTimelineValue(workflowData?.itar?.confidence),
            },
            // Comparative transparency — the competing category/jurisdiction we ruled out.
            ...(workflowData?.itar?.closest_alternative_category ||
            workflowData?.itar?.closest_alternative_reason
              ? [
                  {
                    label: 'Closest Alternative',
                    value: getTimelineValue(
                      [
                        workflowData?.itar?.closest_alternative_category,
                        workflowData?.itar?.closest_alternative_reason,
                      ]
                        .filter(Boolean)
                        .join(' — ')
                    ),
                  },
                ]
              : []),
          ]
        : undefined,
      collapsedBody: hasSettledTimelineStep('itar', itarStatus) ? (
        <ITARResults
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands={false}
          embedded
        />
      ) : undefined,
      expandedBody: hasSettledTimelineStep('itar', itarStatus) ? (
        <ITARResults
          handleRowClick={() => undefined}
          workflowData={workflowData}
          isSelected={false}
          isExpands
          embedded
        />
      ) : undefined,
      onClick: () => handleGotoPhase('itar'),
    },
    {
      label: 'EAR / CCL Check',
      hideDetail: hasSettledTimelineStep('ccl', earStatus),
      detail:
        getTimelineValue(workflowData?.ear_ccl?.license_requirement) ||
        getTimelineValue(workflowData?.ear_ccl?.reasoning),
      status: getTimelineStatus('ccl', earStatus),
      notice: <AssessmentStatusBadge data={workflowData} phase='ccl' variant='pill' />,
      expandedNotice: <AssessmentStatusBadge data={workflowData} phase='ccl' />,
      details: hasSettledTimelineStep('ccl', earStatus)
        ? [
            {
              label: 'Classification',
              value: getTimelineValue(
                workflowData?.ear_ccl?.classification_result,
              ),
            },
            {
              label: 'ECCN',
              value: getTimelineValue(workflowData?.ear_ccl?.eccn),
            },
            ...(workflowData?.ear_ccl?.confidence
              ? [
                  {
                    label: 'Confidence',
                    value: getTimelineValue(workflowData?.ear_ccl?.confidence),
                  },
                ]
              : []),
            {
              label: 'License Requirement',
              value: getTimelineValue(
                workflowData?.ear_ccl?.license_requirement,
              ),
            },
            {
              label: 'Reasoning',
              value: getTimelineValue(workflowData?.ear_ccl?.reasoning),
            },
            // Comparative transparency — the nearest ECCN (or EAR99) we ruled out.
            ...(workflowData?.ear_ccl?.closest_alternative_eccn ||
            workflowData?.ear_ccl?.closest_alternative_reason
              ? [
                  {
                    label: 'Closest Alternative',
                    value: getTimelineValue(
                      [
                        workflowData?.ear_ccl?.closest_alternative_eccn,
                        workflowData?.ear_ccl?.closest_alternative_reason,
                      ]
                        .filter(Boolean)
                        .join(' — ')
                    ),
                  },
                ]
              : []),
          ]
        : undefined,
      collapsedBody: hasSettledTimelineStep('ccl', earStatus) ? (
        <CCLResults
          saveBtn={undefined}
          handleRowClick={() => undefined}
          workflowData={workflowData}
          workflowState={workflowState}
          currentStep={workflowState?._meta?.current_step}
          stepsStatus={workflowState?._meta?.steps_status}
          isSelected={false}
          isExpands={false}
          embedded
        />
      ) : undefined,
      expandedBody: hasSettledTimelineStep('ccl', earStatus) ? (
        <CCLResults
          saveBtn={undefined}
          handleRowClick={() => undefined}
          workflowData={workflowData}
          workflowState={workflowState}
          currentStep={workflowState?._meta?.current_step}
          stepsStatus={workflowState?._meta?.steps_status}
          isSelected={false}
          isExpands
          embedded
        />
      ) : undefined,
      onClick: () => handleGotoPhase('ccl'),
    },
  ];
  // Scoped review: label phases the user didn't request ("Not in this review" — distinct from
  // a Not Applicable skip; clicking still dispatches the goto that re-adds it) and the
  // prerequisites auto-added for a selected check.
  const reviewScope = (workflowData as any)?.review_scope;
  const scopeAutoAdded =
    reviewScope?.scope === 'specific'
      ? scopeAutoIncluded((reviewScope?.selected_phases || []) as ExportScopePhase[])
      : {};
  const exportProgressSteps: WorkflowProgressStep[] = exportProgressStepsBase.map(
    (step, index) => {
      const phase = EXPORT_PHASE_ORDER[index];
      // Not picked on a specific review: "Not in this review" whether it settled as skipped or
      // (an older run) was locked as done by a jurisdiction stop — never "Complete".
      if (
        (step.status === 'skipped' || step.status === 'complete') &&
        isPhaseNotRequested(workflowData, phase)
      ) {
        // No hint line under the card (user, 2026-10-08); clicking the card still adds it.
        return { ...step, value: 'Not in this review' };
      }
      // Locked by an exclusive Nuclear / ITAR result: settled, but never performed.
      const notPerformed =
        step.status === 'complete' ? phaseNotPerformedReason(workflowData, phase) : '';
      if (notPerformed) {
        const note = <span className='export-scope-step-note'>{notPerformed}</span>;
        return {
          ...step,
          status: 'skipped',
          value: 'Not performed',
          notice: note,
          expandedNotice: note,
        };
      }
      const requiredFor = scopeAutoAdded[phase as ExportScopePhase];
      if (requiredFor?.length) {
        const note = (
          <span className='export-scope-step-note'>
            Required for {requiredFor.map((p) => EXPORT_SCOPE_CHIP_LABEL[p]).join(', ')}
          </span>
        );
        return {
          ...step,
          notice: (
            <>
              {step.notice}
              {note}
            </>
          ),
          expandedNotice: (
            <>
              {step.expandedNotice ?? step.notice}
              {note}
            </>
          ),
        };
      }
      return step;
    },
  );

  return (
    <>
      <style>
        {`
          @keyframes earStreamingDot {
            0%, 20% {
              opacity: 0.2;
              transform: translateY(0);
            }
            50% {
              opacity: 1;
              transform: translateY(-1px);
            }
            100% {
              opacity: 0.2;
              transform: translateY(0);
            }
          }

          .streaming-dot {
            display: inline-block;
            min-width: 0.2em;
            line-height: 1;
            vertical-align: baseline;
            animation: earStreamingDot 1s ease-in-out infinite;
          }
        `}
      </style>
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
        <WorkflowPageHeader
          title='Export Workflow'
          extraActions={<CreditMeterButton packType='credits' size='sm' />}
          description='Build a product profile and complete export-control checks in one guided workflow.'
          newActionLabel='New Export'
          onNew={startOver}
          onSave={() => {
            logAppAction('export_workflow:save_results');
            // Mirrors handleOptionClick's 'Save Workflow' case (the in-chat
            // save option): once this workflow already has a ticket, saving
            // again should update it in place, not reopen the "create new
            // saved work" modal — headerSavedWorkflowId is truthy for the
            // life of the ticket even after headerWorkflowIsSaved goes false
            // from continuing the workflow past the last save.
            if (headerSavedWorkflowId) {
              saveTicket(ticketDraft?.ticketName || newTicketName || '');
              return;
            }
            setModalOpen(true);
          }}
          savedPath={
            headerWorkflowIsSaved
              ? `/monitor/ticket/${headerSavedWorkflowId}`
              : undefined
          }
          disableNew={isStreaming || trialLimitReached}
          disableSave={
            !userHasMonitor ||
            !hasUnsavedWorkflowWork ||
            isStreaming ||
            savingTicket ||
            trialLimitReached
          }
        />
      <WorkflowNotice>
        <p>
          This workflow is provided for informational and planning purposes and
          does not constitute a formal EAR, ITAR, jurisdictional or licensing
          determination. Results may be incomplete and must be independently
          verified with qualified export-compliance or legal guidance before
          making export decisions, filings or transactions.
        </p>
      </WorkflowNotice>
      {insufficientCredits && messages.length === 0 && (
        <WorkflowCreditWarning
          remaining={remainingCredits}
          cost={WORKFLOW_CREDIT_COST}
        />
      )}
      <div
        ref={workflowGridRef}
        className='standard-workflow-grid chat-workflow-row export-hub-workflow-grid'
      >
        <div className='standard-workflow-assistant-card bill-container'>
          <WorkflowAssistantHeader
            title='Export Assistant'
            status={
              trialLimitReached ? 'unavailable' : isStreaming ? 'running' : 'idle'
            }
          />
          {!userHasMonitor && (
            <RequiredLicense
              userLicense={normalizedLicense}
              minimumLicense='business'
            />
          )}
          {trialLimitReached ? (
            <TrialRunUsedPanel workflowName='Export Workflow' />
          ) : messages.length > 0 || clfId ? (
            <div className='chat-container'>
              <div
                ref={chatScrollRef}
                className='chat-body standard-workflow-chat-scroll standard-workflow-conversation'
              >
                {messages.map((msg, msgIndex) => msg.type === 'EXPORT_RETURN_DIVIDER' ? (
                  // Back from the Classify tool (slice B): a divider, then the paste card.
                  <div key={msg.id} className='export-return-divider' role='separator'>
                    <span>
                      You opened the Classify tool · <strong>Welcome back</strong>
                    </span>
                  </div>
                ) : (
                  <div
                    key={msg.id}
                    className={`chat-message ${
                      msg.sender === 'user' ? 'user' : 'bot'
                    }`}
                  >
                    {msg.sender === 'bot' && (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='32'
                        height='32'
                        viewBox='0 0 32 32'
                        fill='none'
                      >
                        <g clipPath='url(#clip0_1_40254)'>
                          <path
                            d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z'
                            fill='#4574EE'
                          />
                          <path
                            d='M17.5915 13.6496C16.9135 14.4731 15.4893 14.9301 14.5015 14.4289C13.3459 13.8427 13.0921 11.9548 13.5386 10.8594C14.1072 9.47015 16.0287 8.59755 17.2625 9.68387C18.3252 10.6166 18.488 12.5592 17.5897 13.6486L17.5915 13.6496Z'
                            fill='#23377D'
                          />
                          <path
                            d='M15.6677 10.1001C15.3016 7.89481 14.9878 5.68156 14.7297 3.46243C14.9183 3.46175 15.1052 3.46004 15.2948 3.45758C16.0181 5.34973 16.5393 7.37302 17.0281 9.33704C16.8184 9.6877 16.6096 10.0412 16.4 10.3918C16.1168 10.3046 15.8828 10.1981 15.6688 10.0983L15.6677 10.1001Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M15.9794 5.46298C16.0854 5.80766 16.1883 6.1577 16.2877 6.50568C15.9017 6.45197 15.5028 6.47421 15.1297 6.57564C15.0774 6.19999 15.0286 5.82639 14.979 5.44998C15.3047 5.4141 15.6466 5.4328 15.9812 5.46401L15.9794 5.46298Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M15.5266 10.5469C15.5252 10.5247 15.5227 10.5042 15.5185 10.4827C15.6634 10.533 15.8015 10.6032 15.9283 10.6931C16.6211 11.1812 16.7127 12.0131 16.4608 12.7753C16.3127 13.2258 15.9697 13.6424 15.5542 13.805C15.4665 12.7229 15.5662 11.6299 15.5277 10.5451L15.5266 10.5469Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M14.3623 10.623C14.4416 10.564 14.5248 10.5191 14.6119 10.4837C14.7086 10.4442 14.8098 10.4216 14.9122 10.4093C14.8844 10.4575 14.8687 10.5176 14.872 10.5862C14.9116 11.6691 14.8122 12.7576 14.8973 13.8358C14.782 13.8002 14.6625 13.7431 14.5474 13.6577C14.3375 13.5055 14.1861 13.2847 14.0707 13.0513C13.7178 12.3377 13.6457 11.1503 14.3615 10.6202L14.3623 10.623Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M16.9285 12.3923C16.8894 12.5675 16.9025 12.7799 16.9381 12.9577C17.3502 13.1146 17.8135 13.2987 18.2256 13.4557C18.4536 13.1227 18.5659 12.7135 18.541 12.3109C18.063 12.2969 17.4059 12.3868 16.9275 12.3941L16.9285 12.3923Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M17.3389 11.4576C17.3616 11.5875 17.4327 11.7285 17.5075 11.8384C17.8308 11.8273 18.1973 11.8198 18.5206 11.8087C18.5787 11.5183 18.5398 11.21 18.4079 10.9432C18.0805 11.0687 17.6607 11.317 17.3407 11.4586L17.3389 11.4576Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M8.12142 14.8218C6.99677 18.6559 8.45308 22.4149 12.132 24.103C17.2109 26.4326 25.1027 24.9262 26.6314 19.534C26.8551 18.746 26.9039 17.8403 26.6117 17.0999C26.493 16.7979 26.3189 16.5259 26.0793 16.297C25.3812 15.6367 24.3409 15.5482 23.3983 15.3733C22.5909 15.224 21.7738 14.9761 21.1406 14.4509C20.7407 14.12 20.4334 13.6948 20.0867 13.3089C18.5567 11.6132 16.2637 10.0916 13.9694 9.7651C12.1334 9.50314 10.6679 10.4318 9.62315 11.7916C8.93539 12.6856 8.42955 13.7681 8.11963 14.8208L8.12142 14.8218Z'
                            fill='white'
                          />
                          <path
                            d='M8.28084 14.4393C7.1562 18.2733 8.6125 22.0324 12.2914 23.7204C17.3704 26.0501 25.2621 24.5436 26.7908 19.1514C27.0146 18.3635 27.0633 17.4578 26.7712 16.7174C26.668 17.3129 26.5826 17.8116 26.498 18.02C25.9471 19.3623 24.9208 20.5207 23.657 21.2323C21.4592 22.4719 18.7318 22.4909 16.3099 21.7787C13.6909 21.0075 11.2184 19.2894 10.1836 16.7647C9.494 15.0826 9.4962 13.2257 9.78257 11.409C9.09481 12.3031 8.58897 13.3856 8.27905 14.4383L8.28084 14.4393Z'
                            fill='#EDEDED'
                          />
                          <path
                            d='M26.1648 19.4883C25.1974 19.6492 24.0073 17.2326 24.2273 16.4306C24.3087 16.1369 24.6346 15.9892 24.9344 15.9241C26.0395 15.6855 27.1937 16.0016 27.2202 17.2723C27.2317 17.9127 27.0187 18.583 26.6642 19.1144C26.5119 19.341 26.3423 19.4574 26.1658 19.4865L26.1648 19.4883Z'
                            fill='#23377D'
                          />
                          <path
                            d='M10.2253 16.7191C9.72592 17.6625 8.42206 18.3938 7.35249 18.1003C6.10476 17.7563 5.47582 15.9567 5.69509 14.7969C5.97485 13.3218 7.68172 12.0823 9.10912 12.8992C10.3381 13.6017 10.8844 15.4702 10.2225 16.7199L10.2253 16.7191Z'
                            fill='#23377D'
                          />
                          <path
                            d='M7.63117 13.6256C6.83174 11.5394 6.08257 9.43214 5.38516 7.30954C5.5696 7.27071 5.75405 7.23188 5.9385 7.19306C7.02494 8.90184 7.93942 10.7805 8.81163 12.6086C8.6766 12.9952 8.54158 13.3818 8.40655 13.7683C8.11055 13.7404 7.86075 13.6819 7.63117 13.6256Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M7.0096 9.01929C7.1838 9.33664 7.35311 9.65832 7.51781 9.97973C7.12817 10.0025 6.74202 10.1059 6.39704 10.2784C6.27139 9.92236 6.1478 9.56276 6.02497 9.20597C6.33508 9.1063 6.67647 9.05565 7.0096 9.01929Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M7.58337 14.0935C7.57733 14.071 7.57204 14.0512 7.5629 14.034C7.71509 14.0552 7.86515 14.0966 8.00564 14.1586C8.78119 14.4992 9.03674 15.2947 8.94266 16.0932C8.8866 16.5659 8.63448 17.0397 8.26026 17.2834C7.95947 16.2402 7.83804 15.1505 7.58234 14.0953L7.58337 14.0935Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M6.4554 14.4C6.52176 14.3264 6.59292 14.265 6.67274 14.2134C6.76026 14.1568 6.85438 14.1134 6.95152 14.0814C6.9326 14.1348 6.9308 14.1957 6.94715 14.2623C7.20209 15.3147 7.32277 16.4016 7.62281 17.4419C7.50052 17.4309 7.37397 17.3983 7.24447 17.3379C7.00871 17.2303 6.81551 17.0449 6.65773 16.8395C6.17034 16.2103 5.86105 15.0622 6.45822 14.3993L6.4554 14.4Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M9.3215 15.6213C9.31713 15.8022 9.37511 16.0072 9.44374 16.1731C9.87853 16.2454 10.3698 16.3313 10.8046 16.4037C10.9618 16.0323 10.99 15.6079 10.8858 15.2189C10.415 15.2997 9.78801 15.519 9.3215 15.6213Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M9.53921 14.6222C9.58731 14.7453 9.68531 14.8685 9.7796 14.9611C10.0947 14.8858 10.4508 14.8055 10.766 14.7302C10.7652 14.4343 10.6646 14.138 10.4827 13.9043C10.1875 14.0936 9.82495 14.4203 9.53921 14.6222Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M11.7836 16.1759C12.1634 17.4577 13.3539 18.4952 14.687 18.5716C15.7023 18.629 16.6735 18.1725 17.5478 17.6529C18.422 17.1333 19.2697 16.5316 20.2461 16.252C20.7607 16.1036 21.9847 16.1171 22.3216 15.6327C22.6637 15.1393 22.298 14.3613 21.9624 13.853C21.9039 13.7645 21.8477 13.6844 21.7955 13.6138C21.2395 12.8806 20.5831 12.2181 19.8236 11.7011C18.7473 10.9653 17.4504 10.5 16.1353 10.6484C15.9947 10.6649 15.8521 10.685 15.7117 10.7135C14.5354 10.9349 13.4173 11.5138 12.647 12.4269C11.7834 13.4482 11.4027 14.8959 11.7825 16.1777L11.7836 16.1759Z'
                            fill='#3865BB'
                          />
                          <path
                            d='M11.7829 16.176C12.1628 17.4578 13.3532 18.4953 14.6863 18.5717C15.7016 18.629 16.6729 18.1726 17.5471 17.653C18.4213 17.1334 19.2691 16.5317 20.2454 16.2521C20.76 16.1037 21.9841 16.1172 22.321 15.6328C22.663 15.1394 22.2973 14.3613 21.9617 13.8531C20.8457 13.933 19.3982 14.7315 18.7375 15.1743C17.5624 15.9633 16.3055 16.8361 14.8901 16.7907C14.1971 16.7694 13.4728 16.4846 13.0945 15.9017C12.6501 15.2187 12.7805 14.3078 13.0927 13.5566C13.5738 12.3931 14.4606 11.4019 15.563 10.7925C15.6117 10.7658 15.6605 10.7392 15.7082 10.7143C14.532 10.9357 13.4138 11.5146 12.6435 12.4278C11.7799 13.449 11.3992 14.8967 11.779 16.1785L11.7829 16.176Z'
                            fill='#4488E1'
                          />
                          <path
                            d='M15.2769 13.9217C15.8267 14.5417 15.7991 16.1909 14.9186 15.673C14.5204 15.4383 14.4312 14.9199 14.3535 14.5058C14.2654 14.0309 14.5404 13.4845 15.0687 13.7539C15.1459 13.7936 15.2155 13.8505 15.2779 13.9199L15.2769 13.9217Z'
                            fill='#4CC9F0'
                          />
                          <path
                            d='M19.8962 12.4533C20.3787 12.7319 20.712 13.789 20.041 13.647C19.737 13.5834 19.5699 13.2702 19.433 13.0244C19.2753 12.7404 19.3357 12.3346 19.7288 12.3924C19.7856 12.4014 19.8426 12.4224 19.898 12.4543L19.8962 12.4533Z'
                            fill='#4CC9F0'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_1_40254'>
                            <path
                              d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z'
                              fill='white'
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}

                    {msg.sender === 'bot' &&
                      msg.thoughtSteps &&
                      msg.thoughtSteps.length > 0 &&
                      (() => {
                        const steps = msg.thoughtSteps;
                        const titleStepIndex = getTitleThoughtStepIndex(steps);
                        const titlePhaseKey =
                          titleStepIndex >= 0
                            ? steps[titleStepIndex].phaseKey
                            : undefined;
                        const titleHeading = titlePhaseKey
                          ? EXPORT_PHASE_CHAT_HEADING[titlePhaseKey]
                          : undefined;

                        return (
                          <div className='standard-workflow-thought-block'>
                            {titleHeading && (
                              <div
                                ref={(el) => {
                                  if (titlePhaseKey) {
                                    phaseHeadingRefs.current[titlePhaseKey] =
                                      el;
                                  }
                                }}
                                className='standard-workflow-thought-phase-heading standard-workflow-thought-phase-title'
                              >
                                {titleHeading}
                              </div>
                            )}
                            <div className='standard-workflow-thought-summary standard-workflow-thought-summary-static'>
                              {formatThoughtDuration(msg.thoughtSeconds ?? null)}
                            </div>
                            <div className='standard-workflow-thought-details'>
                              {renderThoughtStepEntries(
                                steps,
                                `${msg.id}`,
                                titleStepIndex,
                              )}
                              <div className='standard-workflow-thought-step standard-workflow-thought-step-done'>
                                <span aria-hidden='true' />
                                <div>Done</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                    {msg.type === 'PHASE_RESULT' ? (
                      <div
                        className='message-bubble'
                        style={{ maxWidth: '90%' }}
                      >
                        {renderPhaseResultCard(msg)}
                      </div>
                    ) : msg.type === 'FOLLOWUP_CARD' && msg.payload?.item ? (
                      <FollowupQuestionCard
                        item={msg.payload.item}
                        active={isFollowupCardActive(msg, msgIndex)}
                        selectedOptionId={msg.payload.selectedOptionId}
                        disabled={isStreaming}
                        initialInputValue={msg.payload.prefill || ''}
                        focusInputOnEscape={!msg.payload.returnCard}
                        onSelect={(option) =>
                          handleFollowupOptionSelect(msg, msg.payload.item, option)
                        }
                        onInputSubmit={(value) =>
                          msg.payload.returnCard
                            ? // The code from the Classify tool answers the pending question as
                              // an HTS code (the server reads the classify_return id).
                              dispatchUserMessage(value, {
                                id: CLASSIFY_RETURN_OPTION_ID,
                                label: value,
                                question: msg.payload.item.question,
                              })
                            : dispatchUserMessage(value)
                        }
                      />
                    ) : msg.type === 'WORKFLOW_CONTROL' ? (
                      renderWorkflowControlOptions(
                        msg,
                        msgIndex === messages.length - 1,
                      )
                    ) : msg.type === 'FINAL_RESULT' ? (
                      (() => {
                        const finalWorkflowData = mergeWorkflowData(
                          workflowData,
                          msg.payload?.workflowData || {},
                        );

                        return (
                      <>
                        <div
                          className='message-bubble'
                          style={{ maxWidth: '90%' }}
                        >
                          <ExportResultsComp
                            name='Schedule B Code:'
                            hts_code={
                              finalWorkflowData?.ear_ccl
                                ?.classification_result || ''
                            }
                            cmdt_desc={
                              finalWorkflowData?.product?.description || ''
                            }
                            explanatory_notes={''}
                            section_chapter_notes={''}
                            cross_data_links={[]}
                            isExpands={true}
                            saveBtn={() => {
                              setModalOpen(true);
                            }}
                            selectedStep={currentStage}
                            handleRowClick={() => {
                              return;
                            }}
                            workflowData={finalWorkflowData}
                          />
                          <p style={{ marginBottom: '5px', marginTop: '5px' }}>
                            {'What would you like to do next?'}
                          </p>
                          <ol className='options-container workflow-control-options'>
                            <li>
                              {renderSaveButton(3, () =>
                                handleOptionClick('Save Work'),
                              )}
                            </li>
                            <li>
                              <button
                                type='button'
                                className='option-button'
                                onClick={() =>
                                  handleOptionClick('New Session')
                                }
                              >
                                New Session
                              </button>
                            </li>
                          </ol>
                        </div>
                      </>
                        );
                      })()
                    ) : msg.text === 'Thinking...' ? (
                      (() => {
                        // Same phase → group → step renderer as the finished
                        // "Thought for Ns" block below, but over the
                        // in-progress accumulator: every progress line
                        // appends underneath the previous one instead of
                        // replacing it, so nothing already narrated
                        // disappears while the turn keeps streaming.
                        const steps = thoughtStepsRef.current;
                        const titleStepIndex = getTitleThoughtStepIndex(steps);
                        const titlePhaseKey =
                          titleStepIndex >= 0
                            ? steps[titleStepIndex].phaseKey
                            : undefined;
                        const titleHeading = titlePhaseKey
                          ? EXPORT_PHASE_CHAT_HEADING[titlePhaseKey]
                          : undefined;

                        return (
                          <div className='message-bubble thinking'>
                            {titleHeading && (
                              <div
                                ref={(el) => {
                                  if (titlePhaseKey) {
                                    phaseHeadingRefs.current[titlePhaseKey] =
                                      el;
                                  }
                                }}
                                className='standard-workflow-thought-phase-heading standard-workflow-thought-phase-title'
                              >
                                {titleHeading}
                              </div>
                            )}
                            <div
                              className='standard-workflow-thought-summary standard-workflow-thought-summary-live'
                              aria-hidden='true'
                            >
                              {formatThoughtDuration(thinkingElapsedSeconds)}
                            </div>
                            <div className='standard-workflow-thought-details'>
                              {renderThoughtStepEntries(
                                steps,
                                'live',
                                titleStepIndex,
                                true,
                              )}
                              <div className='standard-workflow-thought-step standard-workflow-thought-step-thinking'>
                                <span aria-hidden='true' />
                                <div>Thinking…</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className='message-bubble'>
                        <WorkflowStreamingResponse
                          text={msg.text}
                          animate={
                            msg.sender === 'bot' &&
                            msgIndex === messages.length - 1
                          }
                        />
                      </div>
                    )}

                    {msg.sender === 'user' && (
                      <div className='profile-icon'>
                        {cur_user.basics?.firstName[0]}
                        {cur_user.basics?.lastName[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <WorkflowComposer
                inputRef={composerInputRef}
                value={input}
                onChange={setInput}
                onSubmit={handleSend}
                placeholder='Describe what you are exporting, where it is going and who is involved...'
                disabled={isMessageInputDisabled}
                helperText={EXPORT_DATA_BOUNDARY_NOTICE}
              />
            </div>
          ) : (
            <ExportLanding
              key={landingKey}
              inputRef={composerInputRef}
              value={input}
              onChange={setInput}
              onStart={handleLandingStart}
              dataNotice={EXPORT_DATA_BOUNDARY_NOTICE}
              disabled={isMessageInputDisabled || insufficientCredits}
            />
          )}
        </div>
        <div
          className='standard-workflow-progress-card bill-container-fit-screen'
          style={trialLimitReached ? { opacity: 0.55 } : undefined}
        >
          <WorkflowProgressHeader
            title='Classification Progress'
            badge='Beta'
          />
          <WorkflowProgressTimeline
            steps={exportProgressSteps}
            disableExpand
            trailingContent={
              hasWorkflowResults ? (
                <button
                  type='button'
                  className='standard-workflow-primary-btn standard-workflow-progress-action-button'
                  onClick={openPushToHub}
                  disabled={exportDbAvailability === 'none'}
                  title={
                    exportDbAvailability === 'none'
                      ? 'No Export Hub databases exist for this team. Create one in the Export Hub before pushing results.'
                      : undefined
                  }
                  style={{
                    opacity: exportDbAvailability === 'none' ? 0.6 : 1,
                    cursor:
                      exportDbAvailability === 'none'
                        ? 'not-allowed'
                        : undefined,
                  }}
                >
                  Push to Export Hub
                </button>
              ) : undefined
            }
          />
        </div>
      </div>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType={'screening'}
          setFrequency={setFrequency}
          frequency={frequency}
          handleSaveTicket={saveTicket}
          newTicketName={newTicketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={savingTicket}
        />
      </Modal>
      <Dialog
        open={isEndWorkflowModalOpen}
        onClose={() => setIsEndWorkflowModalOpen(false)}
        maxWidth='sm'
        fullWidth
        sx={{ zIndex: 2400 }}
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '520px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          End Workflow
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => setIsEndWorkflowModalOpen(false)}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Would you like to save the workflow before ending? Ending
              without saving will discard your current progress.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={() => setIsEndWorkflowModalOpen(false)}
              >
                <div>Cancel</div>
              </button>
              <button
                className='btn-new-clear'
                onClick={handleEndWorkflowWithoutSaving}
              >
                <div>End Without Saving</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleEndWorkflowSave}
              >
                <div>Save Workflow</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        maxWidth='sm'
        fullWidth
        sx={{ zIndex: 2400 }}
        PaperProps={{
          style: { backgroundColor: 'var(--bg-colors)' },
        }}
      >
        <DialogTitle>
          <div>Push to Export Hub</div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{
              paddingTop: '12px',
              paddingBottom: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            <p className='wizard-body-text' style={{ margin: 0 }}>
              {boundProductId ? (
                <>
                  Updating <strong>{linkedProduct.name || 'the product'}</strong>{' '}
                  in the Export Hub with this workflow&rsquo;s results. Change
                  the database or SKU to target a different product instead.
                </>
              ) : (
                <>
                  Save this workflow&rsquo;s results into an Export Hub product.
                  If no matching product exists in the chosen database, one is
                  created.
                </>
              )}
            </p>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              <span style={pushLabelStyle}>Target database</span>
              {pushLoadingDbs ? (
                <span className='wizard-body-text'>Loading databases…</span>
              ) : pushDatabases.length === 0 ? (
                <span className='wizard-body-text'>
                  No Export Hub databases found for this team.
                </span>
              ) : (
                <select
                  style={pushInputStyle}
                  value={pushDbId}
                  onChange={(e) => setPushDbId(e.target.value)}
                >
                  {pushDatabases.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              <span style={pushLabelStyle}>Product name</span>
              <input
                style={pushInputStyle}
                value={pushName}
                onChange={(e) => setPushName(e.target.value)}
                placeholder='Product name'
              />
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              <span style={pushLabelStyle}>SKU (optional)</span>
              <input
                style={pushInputStyle}
                value={pushSku}
                onChange={(e) => setPushSku(e.target.value)}
                placeholder='Match or create by SKU'
              />
            </label>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={() => setPushOpen(false)}
                disabled={pushing}
              >
                <div>Cancel</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={submitPushToHub}
                disabled={pushing || !pushDbId}
                style={{ opacity: pushing || !pushDbId ? 0.6 : 1 }}
              >
                <div>{pushing ? 'Pushing…' : 'Push to Export Hub'}</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isPhaseRunningModalOpen}
        onClose={() => setIsPhaseRunningModalOpen(false)}
        maxWidth='sm'
        fullWidth
        sx={{ zIndex: 2400 }}
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '520px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Phase In Progress
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => setIsPhaseRunningModalOpen(false)}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              {runningPhaseLabel} is currently running. Please wait until it
              completes before switching to {attemptedPhaseLabel}.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-automate'
                onClick={() => setIsPhaseRunningModalOpen(false)}
              >
                <div>OK</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isLeaveModalOpen}
        onClose={() => {
          setIsLeaveModalOpen(false);
          setPendingLocation(null);
        }}
        maxWidth='sm'
        fullWidth
        sx={{ zIndex: 2400 }}
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave Export Workflow Tool?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => {
              setIsLeaveModalOpen(false);
              setPendingLocation(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this workflow will discard your current work. Use Save
              Ticket to store this transaction in Saved Work before leaving.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={handleLeaveWithoutSaving}
              >
                <div>Leave Without Saving</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleSaveTicketFromLeaveModal}
              >
                <div>Save Ticket</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleLeaveAndOptOut}
              >
                <div>Leave + Don&apos;t Show Again</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* <InsufficientCredits
        isOpen={insufficientCreditsOpen}
        onClose={() => setInsufficientCreditsOpen(false)}
        onGoToPricing={() => {
          window.location.assign('/credit-plans');
        }}
        teamId={cur_user.selected_team.id}
        type='analysis'
        requiredCredits={requiredCredits}
      /> */}
    </>
  );
};

export default EARChatUI;
